/**
 * Admin Page — Full discount code management + platform stats
 */
import { useState, useEffect, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { NavBar } from "@/components/NavBar";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, RotateCcw, Users, Briefcase, Tag,
  Loader2, Calendar, Percent, DollarSign, Pencil, X, Save,
  Ticket, MessageCircle, CheckCircle, Clock, AlertCircle,
  Search, Camera, Building, Star, Shield, ChevronRight, ExternalLink, Mail,
  UserX, UserMinus, AlertTriangle, MapPin, BarChart3, CreditCard, Download, Send,
  EyeOff,
  ShieldCheck,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { USStateMap } from "@/components/USStateMap";

const DISCOUNT_TYPES = [
  { value: "free", label: "100% Free" },
  { value: "percentage", label: "Percentage Off" },
  { value: "fixed_dollar", label: "Dollar Amount Off" },
];

const APPLIES_TO = [
  { value: "job_listing", label: "Job Listings" },
  { value: "subscription", label: "Monthly Subscription" },
  { value: "both", label: "Both" },
];

const STATUS_STYLES = {
  active: "bg-green-500/20 text-green-400",
  inactive: "bg-red-500/20 text-red-400",
  expired: "bg-yellow-500/20 text-yellow-400",
  exhausted: "bg-orange-500/20 text-orange-400",
  scheduled: "bg-blue-500/20 text-blue-400",
};

const EMPTY_FORM = {
  code: "", description: "", discount_type: "free", discount_amount: "",
  applies_to: "job_listing", duration_months: "0", usage_limit: "0", per_user_limit: "0",
  start_date: "", end_date: "",
};

const DURATION_OPTIONS = [
  { value: "0", label: "Forever (all billing cycles)" },
  { value: "1", label: "First month only" },
  { value: "3", label: "3 months" },
  { value: "6", label: "6 months" },
  { value: "12", label: "12 months" },
];

export default function AdminPage() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminJobs, setAdminJobs] = useState([]);
  const [potwSubmissions, setPotwSubmissions] = useState([]);
  const [reviewFunnel, setReviewFunnel] = useState(null);
  const [jobsStatusFilter, setJobsStatusFilter] = useState("all");
  const [jobsSearch, setJobsSearch] = useState("");
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userStateFilter, setUserStateFilter] = useState("all");
  const [userMetroFilter, setUserMetroFilter] = useState("all");
  // QuickBooks sends the browser back here with ?tab=quickbooks after connecting.
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get("tab") || "codes"
  );
  const [loading, setLoading] = useState(true);
  const [mapData, setMapData] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null); // code_id being edited

  const [form, setForm] = useState({ ...EMPTY_FORM });

  const isAdmin = user?.roles?.includes("admin");
  // Server refuses admin endpoints until 2FA is on; the message says where to go
  const [twoFactorMessage, setTwoFactorMessage] = useState(null);

  useEffect(() => { if (isAdmin && token) fetchAll(); }, [isAdmin, token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, codesRes, ticketsRes, mapRes, jobsRes, potwRes, funnelRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }),
        fetch(`${API}/admin/discount-codes`, { headers }),
        fetch(`${API}/support/tickets`, { headers }),
        fetch(`${API}/admin/user-map-data`, { headers }),
        fetch(`${API}/admin/jobs?limit=500`, { headers }),
        fetch(`${API}/admin/potw/submissions`, { headers }),
        fetch(`${API}/admin/reviews/funnel`, { headers }),
      ]);
      if (statsRes.status === 403) {
        const err = await statsRes.json().catch(() => ({}));
        if ((err.detail || "").includes("two-factor")) {
          setTwoFactorMessage(err.detail);
          return;
        }
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (codesRes.ok) setCodes((await codesRes.json()).codes || []);
      if (ticketsRes.ok) setTickets((await ticketsRes.json()).tickets || []);
      if (mapRes.ok) setMapData(await mapRes.json());
      if (jobsRes.ok) setAdminJobs((await jobsRes.json()).jobs || []);
      if (potwRes.ok) setPotwSubmissions((await potwRes.json()).submissions || []);
      if (funnelRes.ok) setReviewFunnel(await funnelRes.json());
    } catch { toast.error("Failed to load admin data"); }
    finally { setLoading(false); }
  };

  // Re-fetch users when state or role filter changes (server-side filtering)
  const fetchFilteredUsers = async () => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      var params = "limit=500";
      if (userStateFilter && userStateFilter !== "all") params += "&state=" + encodeURIComponent(userStateFilter);
      if (userMetroFilter && userMetroFilter !== "all") params += "&metro=" + encodeURIComponent(userMetroFilter);
      if (userRoleFilter && userRoleFilter !== "all") params += "&role=" + encodeURIComponent(userRoleFilter);
      var res = await fetch(API + "/admin/users?" + params, {
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) setAdminUsers((await res.json()).users || []);
    } catch (e) { console.error("Failed to fetch filtered users:", e); }
    finally { setLoadingUsers(false); }
  };

  useEffect(function() {
    if (token) fetchFilteredUsers();
    // token belongs here: this effect is now the only thing that loads the
    // user list, so if it first ran before the token was available nothing
    // would ever fetch it.
  }, [token, userStateFilter, userMetroFilter, userRoleFilter]);

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setShowForm(false);
    setEditingCode(null);
  };

  const startEdit = (code) => {
    setEditingCode(code.code_id);
    setForm({
      code: code.code,
      description: code.description || "",
      discount_type: code.discount_type || code.code_type || "free",
      discount_amount: code.discount_amount ? String(code.discount_amount) : "",
      applies_to: code.applies_to || "job_listing",
      duration_months: String(code.duration_months ?? 0),
      usage_limit: String(code.usage_limit ?? code.max_uses ?? 0),
      per_user_limit: String(code.per_user_limit ?? 0),
      start_date: code.start_date || "",
      end_date: code.end_date || "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!editingCode && !form.code.trim()) return toast.error("Code is required");
    if (form.discount_type !== "free" && !form.discount_amount) return toast.error("Discount amount is required");
    setSaving(true);
    try {
      const body = {
        description: form.description,
        discount_type: form.discount_type,
        discount_amount: form.discount_type === "free" ? 0 : parseFloat(form.discount_amount) || 0,
        applies_to: form.applies_to,
        duration_months: parseInt(form.duration_months) || 0,
        usage_limit: parseInt(form.usage_limit) || 0,
        per_user_limit: parseInt(form.per_user_limit) || 0,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };

      let url, method;
      if (editingCode) {
        url = `${API}/admin/discount-codes/${editingCode}`;
        method = "PUT";
      } else {
        body.code = form.code;
        url = `${API}/admin/discount-codes`;
        method = "POST";
      }

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        toast.success(editingCode ? "Code updated" : `Code "${form.code.toUpperCase()}" created`);
        resetForm();
        fetchAll();
      } else {
        toast.error(data?.detail || "Failed to save code");
      }
    } catch { toast.error("Network error"); }
    finally { setSaving(false); }
  };

  const toggleCode = async (code, active) => {
    const url = active ? `${API}/admin/discount-codes/${code.code_id}` : `${API}/admin/discount-codes/${code.code_id}/activate`;
    try {
      const res = await fetch(url, { method: active ? "DELETE" : "PUT", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success(active ? "Code deactivated" : "Code reactivated"); fetchAll(); }
    } catch { toast.error("Failed to update code"); }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const res = await fetch(`${API}/support/tickets/${ticketId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (res.ok) { toast.success(`Ticket ${ticketId} marked as ${status}`); fetchAll(); }
    } catch { toast.error("Failed to update ticket"); }
  };

  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  if (twoFactorMessage) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <NavBar>
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">Dashboard</Button>
          </Link>
        </NavBar>
        <main className="pt-24 pb-16 px-4 md:px-12">
          <Card className="max-w-md mx-auto bg-white/[0.03] border-white/10 p-8" data-testid="admin-2fa-required">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-5 h-5 text-white/60" />
              <h1 className="text-2xl font-bold">Two-factor authentication required</h1>
            </div>
            <p className="text-white/60 text-sm mb-6">{twoFactorMessage}</p>
            <Link to="/password-reset">
              <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-12">
                Set it up
              </Button>
            </Link>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <NavBar>
        <Link to="/dashboard">
          <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">Dashboard</Button>
        </Link>
      </NavBar>

      <main className="pt-24 pb-16 px-4 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">Dashboard</span>
          </Link>

          <h1 className="text-2xl md:text-4xl font-bold mb-2">Admin Portal</h1>
          <p className="text-white/60 mb-8">Manage discount codes and view platform stats</p>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
          ) : (
            <>
              {/* Stats */}
              {stats && (
                <div className="mb-10">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6" data-testid="admin-stats">
                    <StatCard icon={<Users className="w-5 h-5" />} label="Total Users" value={stats.total_users} />
                    <StatCard icon={<Users className="w-5 h-5" />} label="Studios" value={stats.studios} />
                    <StatCard icon={<Users className="w-5 h-5" />} label="Assistants" value={stats.assistants} />
                    <StatCard icon={<Briefcase className="w-5 h-5" />} label="Total Jobs" value={stats.total_jobs} />
                    <StatCard icon={<Briefcase className="w-5 h-5" />} label="Open Jobs" value={stats.open_jobs} />
                    <StatCard icon={<Tag className="w-5 h-5" />} label="Active Codes" value={stats.active_codes} />
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex flex-wrap gap-1 mb-8 border-b border-white/10 [&>button]:whitespace-nowrap">
                <button onClick={() => setActiveTab("codes")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "codes" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-codes">
                  <Tag className="w-4 h-4 inline mr-1.5" />Discount Codes ({codes.length})
                </button>
                <button onClick={() => setActiveTab("tickets")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "tickets" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-tickets">
                  <Ticket className="w-4 h-4 inline mr-1.5" />Support Tickets ({tickets.filter(t => t.status === "open").length} open)
                </button>
                <button onClick={() => setActiveTab("users")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "users" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-users">
                  <Users className="w-4 h-4 inline mr-1.5" />Users ({stats ? stats.total_users : adminUsers.length})
                </button>
                <button onClick={() => setActiveTab("jobs")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "jobs" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-jobs">
                  <Briefcase className="w-4 h-4 inline mr-1.5" />Jobs ({adminJobs.length})
                </button>
                <button onClick={() => setActiveTab("emails")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "emails" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-emails">
                  <Mail className="w-4 h-4 inline mr-1.5" />Profile Emails
                </button>
                <button onClick={() => setActiveTab("reviews")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "reviews" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-reviews">
                  <Star className="w-4 h-4 inline mr-1.5" />Reviews{reviewFunnel ? " (" + reviewFunnel.summary.never_requested + " not asking)" : ""}
                </button>
                <button onClick={() => setActiveTab("potw")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "potw" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-potw">
                  <Camera className="w-4 h-4 inline mr-1.5" />Photographer of the Week ({potwSubmissions.length})
                </button>
                <button onClick={() => setActiveTab("payments")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "payments" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-payments">
                  <CreditCard className="w-4 h-4 inline mr-1.5" />Payments
                </button>
                <button onClick={() => setActiveTab("quickbooks")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "quickbooks" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-quickbooks">
                  <Building className="w-4 h-4 inline mr-1.5" />QuickBooks
                </button>
                <button onClick={() => setActiveTab("stranded")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "stranded" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-stranded">
                  <AlertTriangle className="w-4 h-4 inline mr-1.5" />Stranded Listings
                </button>
                <button onClick={() => setActiveTab("visibility")} className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "visibility" ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"}`} data-testid="admin-tab-visibility">
                  <EyeOff className="w-4 h-4 inline mr-1.5" />Visibility
                </button>
              </div>

              {activeTab === "codes" && (<>
              {/* Create / Edit Form */}
              {!showForm ? (
                <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold mb-8" data-testid="show-create-form-btn">
                  <Plus className="w-4 h-4 mr-2" />Create Discount Code
                </Button>
              ) : (
                <Card className="bg-white/[0.03] border-white/10 p-6 mb-8" style={{ border: "1px solid rgba(255,255,255,0.12)" }} data-testid="code-form-card">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-bold">{editingCode ? `Edit: ${form.code}` : "Create Discount Code"}</h2>
                    <Button variant="ghost" size="sm" onClick={resetForm} className="text-white/40 hover:text-white"><X className="w-4 h-4 mr-1" />Cancel</Button>
                  </div>

                  <div className="space-y-6">
                    {/* Row 1: Code + Description */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">Code {!editingCode && "*"}</Label>
                        <Input
                          value={form.code}
                          onChange={(e) => updateForm("code", e.target.value.toUpperCase())}
                          placeholder="e.g. SPRING2026"
                          disabled={!!editingCode}
                          className="mt-1.5 bg-white/[0.05] border-white/10 text-white h-11 rounded-sm uppercase font-mono text-lg tracking-wider disabled:opacity-50"
                          data-testid="form-code-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">Description</Label>
                        <Input value={form.description} onChange={(e) => updateForm("description", e.target.value)} placeholder="Internal note about this code" className="mt-1.5 bg-white/[0.05] border-white/10 text-white h-11 rounded-sm" data-testid="form-desc-input" />
                      </div>
                    </div>

                    {/* Row 2: Discount Type + Amount + Applies To */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">Discount Type *</Label>
                        <select value={form.discount_type} onChange={(e) => updateForm("discount_type", e.target.value)} className="mt-1.5 w-full bg-white/[0.05] border border-white/10 text-white h-11 rounded-sm px-3 focus:outline-none focus:border-white/40" data-testid="form-discount-type">
                          {DISCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">
                          {form.discount_type === "percentage" ? "Percentage (%)" : form.discount_type === "fixed_dollar" ? "Dollar Amount ($)" : "Amount (N/A)"}
                        </Label>
                        <div className="relative mt-1.5">
                          <Input
                            type="number" min="0" max={form.discount_type === "percentage" ? 100 : undefined}
                            value={form.discount_amount}
                            onChange={(e) => updateForm("discount_amount", e.target.value)}
                            disabled={form.discount_type === "free"}
                            placeholder={form.discount_type === "free" ? "—" : form.discount_type === "percentage" ? "e.g. 25" : "e.g. 10"}
                            className="bg-white/[0.05] border-white/10 text-white h-11 rounded-sm pr-10 disabled:opacity-30"
                            data-testid="form-discount-amount"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40">
                            {form.discount_type === "percentage" ? <Percent className="w-4 h-4" /> : form.discount_type === "fixed_dollar" ? <DollarSign className="w-4 h-4" /> : null}
                          </span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">Applies To *</Label>
                        <select value={form.applies_to} onChange={(e) => updateForm("applies_to", e.target.value)} className="mt-1.5 w-full bg-white/[0.05] border border-white/10 text-white h-11 rounded-sm px-3 focus:outline-none focus:border-white/40" data-testid="form-applies-to">
                          {APPLIES_TO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Row 2b: Duration for Subscriptions */}
                    {(form.applies_to === "subscription" || form.applies_to === "both") && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-white/50 uppercase tracking-wider">Subscription Duration</Label>
                          <select 
                            value={form.duration_months} 
                            onChange={(e) => updateForm("duration_months", e.target.value)} 
                            className="mt-1.5 w-full bg-white/[0.05] border border-white/10 text-white h-11 rounded-sm px-3 focus:outline-none focus:border-white/40" 
                            data-testid="form-duration-months"
                          >
                            {DURATION_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <p className="text-white/30 text-xs mt-1">How long the discount applies to their subscription</p>
                        </div>
                        <div className="flex items-end">
                          <p className="text-white/40 text-sm pb-3">
                            {form.duration_months === "0" && "Discount applies to all future billing cycles"}
                            {form.duration_months === "1" && "Discount applies only to the first payment"}
                            {form.duration_months !== "0" && form.duration_months !== "1" && `Discount applies to the first ${form.duration_months} payments`}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Row 3: Usage Limits */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">Overall Usage Limit</Label>
                        <Input type="number" min="0" value={form.usage_limit} onChange={(e) => updateForm("usage_limit", e.target.value)} className="mt-1.5 bg-white/[0.05] border-white/10 text-white h-11 rounded-sm" data-testid="form-usage-limit" />
                        <p className="text-white/30 text-xs mt-1">0 = unlimited total uses</p>
                      </div>
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">Per-User Limit</Label>
                        <Input type="number" min="0" value={form.per_user_limit} onChange={(e) => updateForm("per_user_limit", e.target.value)} className="mt-1.5 bg-white/[0.05] border-white/10 text-white h-11 rounded-sm" data-testid="form-per-user-limit" />
                        <p className="text-white/30 text-xs mt-1">0 = unlimited uses per user</p>
                      </div>
                    </div>

                    {/* Row 4: Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">Start Date (optional)</Label>
                        <div className="relative mt-1.5">
                          <Input type="date" value={form.start_date} onChange={(e) => updateForm("start_date", e.target.value)} className="bg-white/[0.05] border-white/10 text-white h-11 rounded-sm pr-12 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-12 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer" data-testid="form-start-date" />
                          <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" />
                        </div>
                        <p className="text-white/30 text-xs mt-1">Leave empty = active immediately</p>
                      </div>
                      <div>
                        <Label className="text-xs text-white/50 uppercase tracking-wider">End Date (optional)</Label>
                        <div className="relative mt-1.5">
                          <Input type="date" value={form.end_date} onChange={(e) => updateForm("end_date", e.target.value)} className="bg-white/[0.05] border-white/10 text-white h-11 rounded-sm pr-12 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-12 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer" data-testid="form-end-date" />
                          <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" />
                        </div>
                        <p className="text-white/30 text-xs mt-1">Leave empty = never expires</p>
                      </div>
                    </div>

                    {/* Summary + Submit */}
                    <div className="border-t border-white/10 pt-5 flex items-center justify-between">
                      <CodeSummary form={form} />
                      <Button onClick={handleSave} disabled={saving || (!editingCode && !form.code.trim())} className="bg-white text-black hover:bg-gray-200 rounded-sm h-11 px-8 font-bold" data-testid="save-code-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCode ? <><Save className="w-4 h-4 mr-2" />Save Changes</> : <><Plus className="w-4 h-4 mr-2" />Create Code</>}
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Codes List */}
              <h2 className="text-lg font-bold mb-4">Discount Codes ({codes.length})</h2>
              {codes.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">No codes yet.</Card>
              ) : (
                <div className="space-y-3" data-testid="codes-list">
                  {codes.map((c) => <CodeRow key={c.code_id} code={c} onToggle={toggleCode} onEdit={startEdit} />)}
                </div>
              )}
              </>)}

              {activeTab === "tickets" && (
                <div data-testid="tickets-panel">
                  <h2 className="text-lg font-bold mb-4">Support Tickets ({tickets.length})</h2>
                  {tickets.length === 0 ? (
                    <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">No tickets yet.</Card>
                  ) : (
                    <div className="space-y-3">
                      {tickets.map((t) => (
                        <Card key={t.ticket_id} className="bg-white/[0.03] border-white/10 p-5" data-testid={`ticket-${t.ticket_id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono font-bold text-[#FF6B6B]">{t.ticket_id}</span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium ${
                                  t.status === "open" ? "bg-amber-500/20 text-amber-400" :
                                  t.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                                  t.status === "resolved" ? "bg-green-500/20 text-green-400" :
                                  "bg-white/10 text-white/40"
                                }`}>
                                  {t.status === "open" && <AlertCircle className="w-3 h-3" />}
                                  {t.status === "in_progress" && <Clock className="w-3 h-3" />}
                                  {t.status === "resolved" && <CheckCircle className="w-3 h-3" />}
                                  {t.status === "closed" && <X className="w-3 h-3" />}
                                  {t.status.replace("_", " ")}
                                </span>
                                <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${
                                  t.source === "member" ? "bg-purple-500/20 text-purple-400" : "bg-white/10 text-white/40"
                                }`}>
                                  {t.source === "member" ? "Member" : "Public"}
                                </span>
                              </div>
                              <div className="text-sm text-white/70 mb-1">
                                <span className="font-medium text-white">{t.name}</span> &middot; <a href={`mailto:${t.email}`} className="text-[#FF6B6B] hover:underline">{t.email}</a>
                              </div>
                              <p className="text-sm text-white/50 whitespace-pre-wrap">{t.message}</p>
                              <div className="text-xs text-white/30 mt-2">
                                {new Date(t.created_at).toLocaleString()}
                                {t.admin_note && <span className="ml-3 text-white/40">Note: {t.admin_note}</span>}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1.5 shrink-0">
                              {t.status !== "in_progress" && <Button size="sm" variant="outline" onClick={() => updateTicketStatus(t.ticket_id, "in_progress")} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs rounded-sm" data-testid={`ticket-progress-${t.ticket_id}`}><Clock className="w-3 h-3 mr-1" />In Progress</Button>}
                              {t.status !== "resolved" && <Button size="sm" variant="outline" onClick={() => updateTicketStatus(t.ticket_id, "resolved")} className="border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs rounded-sm" data-testid={`ticket-resolve-${t.ticket_id}`}><CheckCircle className="w-3 h-3 mr-1" />Resolve</Button>}
                              {t.status !== "closed" && <Button size="sm" variant="outline" onClick={() => updateTicketStatus(t.ticket_id, "closed")} className="border-white/10 text-white/30 hover:bg-white/5 text-xs rounded-sm" data-testid={`ticket-close-${t.ticket_id}`}><X className="w-3 h-3 mr-1" />Close</Button>}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {activeTab === "users" && (
                <div data-testid="users-panel">
                  <UsersByLocationCard
                    stats={stats}
                    mapData={mapData}
                    onSelectState={function(abbr) { setUserMetroFilter("all"); setUserStateFilter(abbr); }}
                    onSelectMetro={function(metroKey) { setUserStateFilter("all"); setUserMetroFilter(metroKey); }}
                  />

                  {/* Search & Filter */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <Input
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Search by name, email, or city..."
                        className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm"
                        data-testid="users-search-input"
                      />
                    </div>
                    <select
                      value={userStateFilter}
                      onChange={(e) => { setUserStateFilter(e.target.value); setUserMetroFilter("all"); }}
                      className="bg-white/[0.05] border border-white/10 text-white h-10 rounded-sm px-3 text-sm"
                      data-testid="users-state-filter"
                    >
                      <option value="all">All States</option>
                      <option value="no_location">No Location</option>
                      {(mapData?.by_state || stats?.users_by_state || []).map(function(s) {
                        return <option key={s.state} value={s.state}>{s.state} ({s.count})</option>;
                      })}
                    </select>
                    <select
                      value={userMetroFilter}
                      onChange={(e) => setUserMetroFilter(e.target.value)}
                      className="bg-white/[0.05] border border-white/10 text-white h-10 rounded-sm px-3 text-sm"
                      data-testid="users-metro-filter"
                    >
                      <option value="all">All Metro Areas</option>
                      {(mapData?.by_metro || [])
                        .filter(function(m) { return userStateFilter === "all" || m.state === userStateFilter; })
                        .map(function(m) {
                          return <option key={m.metro_key} value={m.metro_key}>{m.metro}, {m.state} ({m.count})</option>;
                        })}
                    </select>
                    <select value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)} className="bg-white/[0.05] border border-white/10 text-white h-10 rounded-sm px-3 text-sm" data-testid="users-role-filter">
                      <option value="all">All Roles</option>
                      <option value="studio">Studios</option>
                      <option value="assistant">Second Shooters</option>
                      <option value="admin">Admins</option>
                    </select>
                  </div>

                  <UsersTable users={adminUsers} search={userSearch} roleFilter={userRoleFilter} stateFilter={userStateFilter} onRefresh={fetchAll} />
                </div>
              )}
              {activeTab === "emails" && (
                <ProfileEmailsPanel token={token} stats={stats} />
              )}
              {activeTab === "reviews" && (
                <ReviewFunnelPanel funnel={reviewFunnel} />
              )}
              {activeTab === "potw" && (
                <PotwPanel submissions={potwSubmissions} token={token} onRefresh={fetchAll} />
              )}
              {activeTab === "jobs" && (
                <JobsPanel
                  jobs={adminJobs}
                  search={jobsSearch}
                  setSearch={setJobsSearch}
                  statusFilter={jobsStatusFilter}
                  setStatusFilter={setJobsStatusFilter}
                  loading={loadingJobs}
                />
              )}
              {activeTab === "payments" && (
                <PaymentsPanel token={token} />
              )}
              {activeTab === "quickbooks" && (
                <QuickBooksPanel token={token} />
              )}
              {activeTab === "stranded" && (
                <StrandedListingsPanel token={token} />
              )}
              {activeTab === "visibility" && (
                <VisibilityPanel token={token} />
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

/* ── Code Summary Preview ───────────────────── */
function CodeSummary({ form }) {
  const desc = [];
  if (form.discount_type === "free") desc.push("100% free");
  else if (form.discount_type === "percentage") desc.push(`${form.discount_amount || "?"}% off`);
  else if (form.discount_type === "fixed_dollar") desc.push(`$${form.discount_amount || "?"} off`);
  desc.push(form.applies_to === "both" ? "jobs & subscriptions" : form.applies_to === "subscription" ? "subscriptions" : "job listings");
  const ul = form.usage_limit && form.usage_limit !== "0" ? `${form.usage_limit} total` : "unlimited";
  const pl = form.per_user_limit && form.per_user_limit !== "0" ? `${form.per_user_limit}/user` : "unlimited/user";
  desc.push(ul);
  desc.push(pl);
  return (
    <p className="text-white/40 text-sm" data-testid="code-summary">
      {form.code ? <span className="text-white font-mono font-bold">{form.code}</span> : "..."}{" "}— {desc.join(" · ")}
    </p>
  );
}

/* ── Code Row ───────────────────────────────── */
function CodeRow({ code: c, onToggle, onEdit }) {
  const statusStyle = STATUS_STYLES[c.status] || STATUS_STYLES.active;
  const dt = c.discount_type || c.code_type || "free";
  const discountLabel = dt === "free" || dt === "free_job" ? "FREE" : dt === "percentage" ? `${c.discount_amount}% off` : dt === "fixed_dollar" ? `$${c.discount_amount} off` : "—";
  const appliesToLabel = c.applies_to === "both" ? "Jobs & Subs" : c.applies_to === "subscription" ? "Subscriptions" : "Job Listings";
  const usageLimit = c.usage_limit || c.max_uses || 0;
  const perUser = c.per_user_limit ?? 0;

  return (
    <Card className={`bg-white/[0.03] border-white/10 ${!c.active ? "opacity-50" : ""}`} style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid={`code-row-${c.code}`}>
      <div className="p-4">
        {/* Top line: code name, status, discount */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-lg tracking-wider">{c.code}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${statusStyle}`}>{c.status}</span>
            <span className="text-coral text-sm font-semibold">{discountLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onEdit(c)} className="text-white/50 hover:text-white hover:bg-white/10" data-testid={`edit-code-${c.code}`}>
              <Pencil className="w-3.5 h-3.5 mr-1" />Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => onToggle(c, c.active)} className="border-white/20 text-white/60 hover:bg-white/10 shrink-0" data-testid={`toggle-code-${c.code}`}>
              {c.active ? <><Trash2 className="w-3.5 h-3.5 mr-1" />Deactivate</> : <><RotateCcw className="w-3.5 h-3.5 mr-1" />Reactivate</>}
            </Button>
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-2 text-sm text-white/50">
          <div><span className="text-white/30 text-xs">Used:</span> {c.times_used}{usageLimit > 0 ? ` / ${usageLimit}` : ""}</div>
          <div><span className="text-white/30 text-xs">Applies to:</span> {appliesToLabel}</div>
          <div><span className="text-white/30 text-xs">Per user:</span> {perUser === 0 ? "Unlimited" : perUser}</div>
          <div><span className="text-white/30 text-xs">Start:</span> {c.start_date || "Immediately"}</div>
          <div><span className="text-white/30 text-xs">End:</span> {c.end_date || "Never"}</div>
        </div>
        {c.description && <p className="text-white/30 text-xs mt-2">{c.description}</p>}
      </div>
    </Card>
  );
}

/**
 * Metro-area breakdown of the user base, grouped by state.
 *
 * States are large enough that a raw state count hides the answer to "can we
 * promote here yet?" — a shooter in Miami is no use to a studio in St. Pete.
 * Each row rolls a city and its suburbs up into one market, with the
 * studio/shooter split that decides whether the market is worth launching to.
 */
/** Remembers a panel's open/closed state between visits. */
function useSticky(key, fallback) {
  const [value, setValue] = useState(function () {
    try {
      const stored = window.localStorage.getItem(key);
      return stored === null ? fallback : stored === "1";
    } catch {
      return fallback;
    }
  });
  const update = function (next) {
    setValue(next);
    try { window.localStorage.setItem(key, next ? "1" : "0"); } catch { /* private window */ }
  };
  return [value, update];
}

function CollapsibleSection({ title, subtitle, open, onToggle, testId, children }) {
  return (
    <div className="border-t border-white/[0.07] first:border-t-0">
      <button
        type="button"
        onClick={function () { onToggle(!open); }}
        className="w-full flex items-center gap-2 py-3 text-left group"
        data-testid={testId}
        aria-expanded={open}
      >
        <ChevronRight
          className={"w-4 h-4 text-white/30 transition-transform " + (open ? "rotate-90" : "")}
        />
        <span className="text-xs uppercase tracking-wider font-medium text-white/50 group-hover:text-white/80">
          {title}
        </span>
        {subtitle && <span className="text-xs text-white/25">{subtitle}</span>}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

/**
 * Where the user base is, shown inside the Users tab.
 *
 * The map and the metro list are the two tall things here, so each collapses
 * independently and the choice sticks — this sits above the user list that is
 * usually what you came to the tab for.
 */
function UsersByLocationCard({ stats, mapData, onSelectState, onSelectMetro }) {
  const [mapOpen, setMapOpen] = useSticky("admin.usersByLocation.map", false);
  const [metrosOpen, setMetrosOpen] = useSticky("admin.usersByLocation.metros", false);

  if (!stats) return null;

  const stateCount = (stats.users_by_state || []).length;
  const metroCount = (mapData?.by_metro || []).length;

  return (
    <Card className="bg-white/[0.03] border-white/10 p-5 mb-6" data-testid="users-by-state-card">
      <div className="flex items-center gap-2 text-white/40 mb-3">
        <MapPin className="w-4 h-4" />
        <span className="text-xs uppercase tracking-wider font-medium">Users by Location</span>
      </div>

      {/* State badges stay visible — they are one compact row and double as filters */}
      <div className="flex flex-wrap gap-2 mb-2">
        {(stats.users_by_state || []).map(function (s) {
          var mapEntry = (mapData?.by_state || []).find(function (m) { return m.state === s.state; });
          var cities = mapEntry?.cities || [];
          return (
            <button
              key={s.state}
              onClick={function () { onSelectState(s.state); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.05] border border-white/10 rounded-sm text-sm hover:bg-white/10 hover:border-white/20 transition-colors cursor-pointer group"
              data-testid={"state-" + s.state.toLowerCase()}
              title={cities.length > 0 ? cities.map(function (c) { return c.city + ": " + c.count; }).join(", ") : ""}
            >
              <span className="text-white/70 group-hover:text-white">{s.state}</span>
              <span className="text-white font-bold">{s.count}</span>
            </button>
          );
        })}
        {stats.no_location_count > 0 && (
          <button
            onClick={function () { onSelectState("no_location"); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-sm text-sm hover:bg-red-500/20 hover:border-red-500/30 transition-colors cursor-pointer"
            data-testid="state-no-location"
          >
            <span className="text-red-400/80">No Location</span>
            <span className="text-red-400 font-bold">{stats.no_location_count}</span>
          </button>
        )}
      </div>

      <CollapsibleSection
        title="Map"
        subtitle={stateCount ? stateCount + " states" : ""}
        open={mapOpen}
        onToggle={setMapOpen}
        testId="toggle-user-map"
      >
        {mapData ? (
          <USStateMap stateData={mapData.by_state} onStateClick={onSelectState} />
        ) : (
          <p className="text-sm text-white/30">Map data unavailable.</p>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Metro areas"
        subtitle={metroCount ? metroCount + " markets" : ""}
        open={metrosOpen}
        onToggle={setMetrosOpen}
        testId="toggle-metro-areas"
      >
        <MetroBreakdown metros={mapData?.by_metro} onSelect={onSelectMetro} />
      </CollapsibleSection>
    </Card>
  );
}


function MetroBreakdown({ metros, onSelect }) {
  const [showAll, setShowAll] = useState(false);

  const grouped = useMemo(
    function () {
      const byState = {};
      (metros || []).forEach(function (m) {
        if (!byState[m.state]) byState[m.state] = { state: m.state, count: 0, metros: [] };
        byState[m.state].count += m.count;
        byState[m.state].metros.push(m);
      });
      return Object.values(byState).sort(function (a, b) { return b.count - a.count; });
    },
    [metros]
  );

  if (grouped.length === 0) return null;

  const VISIBLE_STATES = 8;
  const visible = showAll ? grouped : grouped.slice(0, VISIBLE_STATES);

  return (
    // The heading lives on the collapsible section that wraps this.
    <div data-testid="metro-breakdown">
      <p className="text-[11px] text-white/25 text-right mb-3">studios &middot; shooters &middot; total</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
        {visible.map(function (group) {
          return (
            <div key={group.state}>
              <div className="flex items-baseline justify-between mb-1.5 pb-1 border-b border-white/[0.07]">
                <span className="text-[11px] font-semibold tracking-widest text-white/50">{group.state}</span>
                <span className="text-[11px] text-white/30">{group.count}</span>
              </div>
              {group.metros.map(function (m) {
                return (
                  <button
                    key={m.metro_key}
                    onClick={function () { onSelect(m.metro_key); }}
                    className="w-full flex items-baseline gap-2 px-1.5 py-1 -mx-1.5 rounded-sm hover:bg-white/[0.06] transition-colors text-left group"
                    data-testid={"metro-" + m.metro_key}
                    title={(m.cities || []).map(function (c) { return c.city + ": " + c.count; }).join(", ")}
                  >
                    <span className="flex-1 truncate text-sm text-white/70 group-hover:text-white">
                      {m.metro}
                      {!m.is_metro && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-white/25">city</span>
                      )}
                    </span>
                    <span className="text-[11px] text-white/35 tabular-nums shrink-0">
                      {m.studios} · {m.assistants}
                    </span>
                    <span className="w-7 text-right text-sm font-bold text-white tabular-nums shrink-0">{m.count}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {grouped.length > VISIBLE_STATES && (
        <button
          onClick={function () { setShowAll(!showAll); }}
          className="mt-4 text-xs text-[#FF6B6B] hover:text-[#ff8585] transition-colors"
          data-testid="metro-toggle-all"
        >
          {showAll ? "Show fewer states" : "Show all " + grouped.length + " states"}
        </button>
      )}
    </div>
  );
}


function StatCard({ icon, label, value }) {
  return (
    <Card className="bg-white/[0.03] border-white/10 p-4">
      <div className="flex items-center gap-2 text-white/40 mb-1">
        {icon}<span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </Card>
  );
}

/* ── Profile Emails Panel ────────────────────── */

/* ── Payments Panel ────────────────────── */
/* ── Stranded job listings ──────────────────── */
function StrandedListingsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState("");

  useEffect(function () {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await fetch(API + "/admin/stranded-listings", {
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) setData(await res.json());
      else toast.error("Could not load job listings");
    } catch (e) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }

  async function publish(job, gift) {
    const opening = gift
      ? `Gift the $10 listing fee for "${job.title}" and publish it now?\n\n` +
        `They will not be charged.\n\n`
      : `Publish "${job.title}" now?\n\n`;
    if (!window.confirm(
      opening +
      `It will appear in search straight away, and second shooters within ` +
      `50 miles who have job alerts switched on will be emailed about it.`
    )) return;

    setPublishing(job.job_id);
    try {
      const res = await fetch(API + "/admin/stranded-listings/" + job.job_id + "/publish", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
      });
      const body = await res.json().catch(function () { return {}; });
      if (res.ok) {
        toast.success(body.gifted ? `"${job.title}" is live — fee waived` : `"${job.title}" is live`);
        fetchData();
      } else {
        toast.error(body.detail || "Could not publish that listing");
      }
    } catch (e) {
      toast.error("Network error");
    } finally {
      setPublishing("");
    }
  }

  const fmt = function (d) {
    if (!d) return "—";
    const p = new Date(d);
    // Rendered in UTC on purpose. These are stored as UTC midnight, and a
    // local-time render puts every date a day early west of Greenwich — an
    // event on the 23rd reading as the 22nd is worse than useless here.
    return isNaN(p.getTime())
      ? "—"
      : p.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  };

  if (loading) {
    return (
      <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Checking job listings…
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">
        Could not load job listings.{" "}
        <button onClick={fetchData} className="text-[#FF6B6B] hover:underline">Try again</button>
      </Card>
    );
  }

  function Group({ title, blurb, rows, tone, canPublish, gift }) {
    if (!rows.length) return null;
    return (
      <Card className={`bg-white/[0.03] border-white/10 p-5 mb-6 ${tone === "bad" ? "border-l-2 border-l-red-500/50" : ""}`}>
        <h3 className="text-sm font-medium mb-1">{title} <span className="text-white/40">({rows.length})</span></h3>
        <p className="text-white/40 text-sm mb-4">{blurb}</p>
        <div className="space-y-2">
          {rows.map(function (j) {
            return (
              <div key={j.job_id} className="flex flex-wrap items-center justify-between gap-3 py-2.5 border-b border-white/[0.05] last:border-0">
                <div className="min-w-0">
                  <p className="text-sm text-white/90">{j.title || "Untitled"}</p>
                  <p className="text-white/40 text-xs mt-0.5">
                    {j.studio_name}
                    {j.studio_email && <span className="text-white/25"> · {j.studio_email}</span>}
                  </p>
                  <p className="text-white/30 text-xs mt-0.5">
                    Posted {fmt(j.created_at)} · Event {fmt(j.event_date)} · {j.reason}
                  </p>
                </div>
                {canPublish && (
                  j.event_passed ? (
                    <span className="text-xs text-white/30 shrink-0">event has passed</span>
                  ) : (
                    <Button
                      onClick={function () { publish(j, gift); }}
                      disabled={publishing === j.job_id}
                      className="bg-white/[0.05] hover:bg-white/10 text-white border border-white/10 h-8 rounded-sm text-xs shrink-0"
                      data-testid={`publish-${j.job_id}`}
                    >
                      {publishing === j.job_id
                        ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Publishing…</>
                        : gift ? "Gift listing & publish" : "Publish now"}
                    </Button>
                  )
                )}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  const nothingWrong =
    data.paid_not_live.length === 0 && data.money_arrived.length === 0;

  return (
    <div data-testid="stranded-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={<AlertTriangle className="w-5 h-5" />} label="Paid, never live" value={data.paid_not_live.length} />
        <StatCard icon={<CreditCard className="w-5 h-5" />} label="Charged, never live" value={data.money_arrived.length} />
        <StatCard icon={<Clock className="w-5 h-5" />} label="Abandoned" value={data.abandoned.length} />
        <StatCard icon={<Briefcase className="w-5 h-5" />} label="Live now" value={data.live} />
      </div>

      <Group
        tone="bad"
        title="Someone was charged and never got their listing"
        blurb="A completed Stripe payment with nothing published. These people are owed a listing or a refund — check each against Stripe, then publish it or pay them back."
        rows={data.money_arrived}
        canPublish
      />
      <Group
        title="Marked paid, never published"
        blurb="Usually a discount code or an auto-free listing. Nothing was charged, but the studio was told the posting worked and it never appeared."
        rows={data.paid_not_live}
        canPublish
      />
      <Group
        title="Started checkout, never paid"
        blurb="Nothing was charged. They were told the job posted successfully and never learned it was not live, so gifting the fee is a reasonable way to make good. Publishing waives the $10 — it is recorded as gifted, not as revenue."
        rows={data.abandoned}
        canPublish
        gift
      />

      {nothingWrong && (
        <Card className="bg-white/[0.03] border-white/10 p-8 text-center">
          <CheckCircle className="w-6 h-6 text-green-400/70 mx-auto mb-2" />
          <p className="text-white/70 mb-1">Nothing stranded.</p>
          <p className="text-white/40 text-sm">Every listing that was paid for is live.</p>
        </Card>
      )}

      <p className="text-white/30 text-xs mt-6">
        Checked {data.total_jobs} listings. Publishing sends job-alert emails to second
        shooters within 50 miles, so listings whose event has passed cannot be published.
      </p>
      <Button
        onClick={fetchData}
        className="mt-4 bg-white/[0.05] hover:bg-white/10 text-white border border-white/10 h-9 rounded-sm"
        data-testid="stranded-refresh"
      >
        <RotateCcw className="w-4 h-4 mr-1.5" />Refresh
      </Button>
    </div>
  );
}

/* ── Visibility mismatch ────────────────────── */
function VisibilityPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(function () {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await fetch(API + "/admin/visibility-mismatch", {
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("Failed to load visibility data");
      }
    } catch (e) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Checking who is still visible…
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">
        Could not load visibility data.{" "}
        <button onClick={fetchData} className="text-[#FF6B6B] hover:underline">Try again</button>
      </Card>
    );
  }

  const rows = (data.users || []).filter(function (u) {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || "").toLowerCase().indexOf(q) !== -1
      || (u.email || "").toLowerCase().indexOf(q) !== -1;
  });

  const exportCsv = function () {
    const header = ["Name", "Email", "Roles", "Still visible", "Created", "Last active"];
    const body = rows.map(function (u) {
      return [u.name, u.email, (u.roles || []).join(" "), (u.reasons || []).join("; "),
              u.created_at, u.last_active];
    });
    const csv = [header].concat(body)
      .map(function (r) {
        return r.map(function (c) { return '"' + String(c == null ? "" : c).replace(/"/g, '""') + '"'; }).join(",");
      })
      .join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "still-visible.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const fmt = function (d) {
    if (!d) return "—";
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? "—" : parsed.toISOString().slice(0, 10);
  };

  return (
    <div data-testid="visibility-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Still visible"
          value={data.affected}
        />
        <StatCard
          icon={<EyeOff className="w-5 h-5" />}
          label="Asked to be hidden"
          value={data.asked_to_hide}
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          label="Hidden correctly"
          value={data.already_hidden}
        />
        <StatCard
          icon={<UserX className="w-5 h-5" />}
          label="Deactivated anyway"
          value={data.deactivated}
        />
      </div>

      <Card className="bg-white/[0.03] border-white/10 p-5 mb-6">
        <div className="flex items-center gap-2 text-white/40 mb-3">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wider font-medium">What this is</span>
        </div>
        <p className="text-sm text-white/60">
          The active flag used to be one field for the whole account. It was split into a
          separate one per role, and searches moved with it — but two switches kept writing
          the old field, which nothing reads any more. Anyone below asked to be hidden and
          is still turning up in search and still getting hiring alerts. Some clicked the
          broken switch; others hid themselves correctly before the split and were stranded
          by it.
        </p>
        <p className="text-sm text-white/50 mt-3 pt-3 border-t border-white/[0.07]">
          The switches are fixed, so this list only ever shrinks. It counts people stranded
          before the fix — nobody new can land here.
        </p>
      </Card>

      {data.affected === 0 ? (
        <Card className="bg-white/[0.03] border-white/10 p-8 text-center">
          <CheckCircle className="w-6 h-6 text-green-400/70 mx-auto mb-2" />
          <p className="text-white/70 mb-4">Nobody is stranded. Everyone who asked to be hidden is hidden.</p>
          <Button
            onClick={fetchData}
            className="bg-white/[0.05] hover:bg-white/10 text-white border border-white/10 h-9 rounded-sm"
            data-testid="visibility-refresh-empty"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />Check again
          </Button>
        </Card>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm"
                data-testid="visibility-search"
              />
            </div>
            <Button
              onClick={exportCsv}
              className="bg-white/[0.05] hover:bg-white/10 text-white border border-white/10 h-10 rounded-sm"
              data-testid="visibility-export"
            >
              <Download className="w-4 h-4 mr-1.5" />Export CSV
            </Button>
            <Button
              onClick={fetchData}
              className="bg-white/[0.05] hover:bg-white/10 text-white border border-white/10 h-10 rounded-sm"
              data-testid="visibility-refresh"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" />Refresh
            </Button>
          </div>

          <Card className="bg-white/[0.03] border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider">
                    <th className="text-left font-medium px-4 py-3">Name</th>
                    <th className="text-left font-medium px-4 py-3">Email</th>
                    <th className="text-left font-medium px-4 py-3">Still visible</th>
                    <th className="text-left font-medium px-4 py-3">Joined</th>
                    <th className="text-left font-medium px-4 py-3">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(function (u) {
                    return (
                      <tr key={u.user_id} className="border-b border-white/[0.05] last:border-0">
                        <td className="px-4 py-3">
                          <Link
                            to={"/profile/" + u.user_id}
                            className="text-white/85 hover:text-white hover:underline"
                          >
                            {u.name || "—"}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-white/60">{u.email || "—"}</td>
                        <td className="px-4 py-3">
                          {(u.reasons || []).map(function (r) {
                            return (
                              <Badge
                                key={r}
                                className="mr-1 bg-amber-500/10 text-amber-300/80 border-amber-500/20 font-normal"
                              >
                                {r}
                              </Badge>
                            );
                          })}
                        </td>
                        <td className="px-4 py-3 text-white/40">{fmt(u.created_at)}</td>
                        <td className="px-4 py-3 text-white/40">{fmt(u.last_active)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          {rows.length === 0 && (
            <p className="text-white/30 text-sm text-center mt-4">No one matches that search.</p>
          )}
        </>
      )}
    </div>
  );
}

/* ── QuickBooks ─────────────────────────────── */
function QuickBooksPanel({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [smokeResult, setSmokeResult] = useState(null);
  const [summary, setSummary] = useState(null);
  const [logRows, setLogRows] = useState([]);
  const [backfillDays, setBackfillDays] = useState("90");
  const [setupPreview, setSetupPreview] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [taxjar, setTaxjar] = useState(null);
  const [payoutAccount, setPayoutAccount] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/quickbooks/status`, { headers });
      if (!res.ok) throw new Error(`Status check failed (${res.status})`);
      setStatus(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSync = async () => {
    try {
      const [sumRes, logRes] = await Promise.all([
        fetch(`${API}/quickbooks/summary`, { headers }),
        fetch(`${API}/quickbooks/sync-log?limit=50`, { headers }),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (logRes.ok) setLogRows((await logRes.json()).rows || []);
    } catch {
      /* the status card already reports connection trouble */
    }
  };

  // The OAuth callback lands back here with the outcome in the query string.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("qbo");
    if (outcome === "connected") toast.success("Connected to QuickBooks");
    if (outcome === "error") toast.error(params.get("detail") || "Could not connect to QuickBooks");
    if (outcome) {
      window.history.replaceState({}, "", `${window.location.pathname}?tab=quickbooks`);
    }
    loadStatus();
    loadSync();
    loadSetupPreview({ quiet: true });
    loadPayoutSettings();
    loadTaxjar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSetupPreview = async ({ quiet } = {}) => {
    if (!quiet) setBusy("preview");
    try {
      const res = await fetch(`${API}/quickbooks/setup/preview`, { headers });
      if (!res.ok) throw new Error("Could not read the QuickBooks setup");
      setSetupPreview(await res.json());
    } catch (e) {
      if (!quiet) toast.error(e.message);
    } finally {
      if (!quiet) setBusy("");
    }
  };

  const createMissing = async () => {
    const count = setupPreview?.to_create ?? 0;
    if (!window.confirm(
      `Create ${count} record${count === 1 ? "" : "s"} in QuickBooks?\n\n` +
      `Nothing existing is changed or deleted — only missing records are added.`
    )) return;

    setBusy("create-missing");
    try {
      const res = await fetch(`${API}/quickbooks/setup/create-missing`, { method: "POST", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Setup failed");
      if (data.ok) toast.success(`Created ${data.created} record${data.created === 1 ? "" : "s"} in QuickBooks`);
      else toast.error("Some records could not be created — see below");
      await loadSetupPreview({ quiet: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const loadTaxjar = async () => {
    try {
      const res = await fetch(`${API}/admin/taxjar/status`, { headers });
      if (res.ok) setTaxjar(await res.json());
    } catch {
      /* shown as unknown rather than blocking the rest of the tab */
    }
  };

  const loadPayoutSettings = async () => {
    try {
      const [accountsRes, settingsRes] = await Promise.all([
        fetch(`${API}/quickbooks/bank-accounts`, { headers }),
        fetch(`${API}/quickbooks/settings`, { headers }),
      ]);
      if (accountsRes.ok) setBankAccounts((await accountsRes.json()).accounts || []);
      if (settingsRes.ok) setPayoutAccount((await settingsRes.json()).payout_account_id || "");
    } catch {
      /* the status card already reports connection trouble */
    }
  };

  const savePayoutAccount = async (accountId) => {
    const account = bankAccounts.find((a) => a.id === accountId);
    setBusy("payout-account");
    try {
      const params = new URLSearchParams({
        payout_account_id: accountId,
        payout_account_name: account?.name || "",
      });
      const res = await fetch(`${API}/quickbooks/settings?${params}`, { method: "POST", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not save the payout account");
      setPayoutAccount(accountId);
      toast.success(`Stripe payouts will transfer into ${account?.name}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const runSync = async (path, label, options = {}) => {
    setBusy(path);
    try {
      const res = await fetch(`${API}/quickbooks/${path}`, { method: "POST", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `${label} failed`);
      toast.success(options.describe ? options.describe(data) : `${label} finished`);
      await loadSync();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const connect = async () => {
    setBusy("connect");
    try {
      const res = await fetch(`${API}/quickbooks/connect`, { method: "POST", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not start the connection");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.message);
      setBusy("");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? Sales will stop syncing until you reconnect.")) return;
    setBusy("disconnect");
    try {
      const res = await fetch(`${API}/quickbooks/disconnect`, { method: "POST", headers });
      if (!res.ok) throw new Error("Disconnect failed");
      toast.success("Disconnected from QuickBooks");
      setSmokeResult(null);
      await loadStatus();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const runSandbox = async (path, setResult, label) => {
    setBusy(path);
    setResult(null);
    try {
      const res = await fetch(`${API}/quickbooks/sandbox/${path}`, { method: "POST", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `${label} failed`);
      setResult(data);
      if (data.ok) toast.success(`${label} succeeded`);
      else toast.error(`${label} came back with problems — see below`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white/50 py-12" data-testid="qbo-loading">
        <Loader2 className="w-4 h-4 animate-spin" />Checking the QuickBooks connection…
      </div>
    );
  }

  const isSandbox = status?.environment === "sandbox";

  return (
    <div className="space-y-6" data-testid="quickbooks-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            QuickBooks
            <Badge className={isSandbox ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}>
              {status?.environment || "unknown"}
            </Badge>
          </h2>
          <p className="text-sm text-white/50 mt-1">
            Sales receipts post here automatically, net of the Stripe fee.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadStatus} className="text-white/40 hover:text-white" data-testid="qbo-refresh">
          <RotateCcw className="w-4 h-4 mr-1.5" />Refresh
        </Button>
      </div>

      {!status?.configured && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="qbo-not-configured">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Not configured yet</p>
              <p className="text-sm text-white/50 mb-3">
                Add these to the backend environment, then restart it:
              </p>
              <ul className="text-sm font-mono text-white/70 space-y-1">
                {(status?.missing_config || []).map((key) => <li key={key}>{key}</li>)}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {status?.configured && !status?.connected && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="qbo-disconnected">
          <p className="font-medium mb-1">Not connected</p>
          <p className="text-sm text-white/50 mb-4">
            You'll be sent to Intuit to sign in and choose which company to link.
          </p>
          <Button onClick={connect} disabled={busy === "connect"} className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold" data-testid="qbo-connect-btn">
            {busy === "connect"
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opening Intuit…</>
              : <><ExternalLink className="w-4 h-4 mr-2" />Connect to QuickBooks</>}
          </Button>
        </Card>
      )}

      {status?.connected && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="qbo-connected">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              {status.healthy
                ? <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                : <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />}
              <div>
                <p className="font-medium">{status.company_name || "Connected"}</p>
                <p className="text-sm text-white/50 font-mono mt-0.5">Realm {status.realm_id}</p>
                {!status.healthy && status.last_error && (
                  <p className="text-sm text-red-400 mt-2 max-w-md">{status.last_error}</p>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={disconnect} disabled={busy === "disconnect"} className="text-white/40 hover:text-red-400" data-testid="qbo-disconnect-btn">
              <X className="w-4 h-4 mr-1.5" />Disconnect
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
            <QboFact label="Connected" value={fmtWhen(status.connected_at)} />
            <QboFact label="Access token renews" value={fmtWhen(status.access_expires_at)} />
            <QboFact label="Sign-in valid until" value={fmtWhen(status.refresh_expires_at)} />
          </div>
        </Card>
      )}

      {status?.connected && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="qbo-setup">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="font-medium">Chart of accounts</p>
              <p className="text-sm text-white/50 mt-0.5">
                Receipts match items by exact name, so these are created from the same
                names the sync uses. Nothing existing is changed or deleted.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => loadSetupPreview()} disabled={!!busy} className="text-white/40 hover:text-white" data-testid="qbo-preview-btn">
              {busy === "preview" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
              Check
            </Button>
          </div>

          {setupPreview?.nothing_to_do && (
            <p className="text-sm text-green-400 flex items-center gap-2" data-testid="qbo-setup-ready">
              <CheckCircle className="w-4 h-4" />
              Everything the sync needs is already in QuickBooks.
            </p>
          )}

          {setupPreview && !setupPreview.nothing_to_do && (
            <>
              <p className="text-sm text-white/70 mb-3">
                {setupPreview.to_create} record{setupPreview.to_create === 1 ? "" : "s"} missing:
              </p>
              <ul className="text-sm space-y-1 mb-5">
                {[
                  ...setupPreview.accounts.map((a) => ({ ...a, kind: "Account" })),
                  ...setupPreview.items.map((i) => ({ ...i, kind: "Item" })),
                  { ...setupPreview.customer, kind: "Customer" },
                ]
                  .filter((r) => r.action === "would create")
                  .map((r) => (
                    <li key={`${r.kind}-${r.name}`} className="text-white/70">
                      <span className="text-white/40 text-[11px] uppercase tracking-wider mr-2">{r.kind}</span>
                      {r.name}
                      {r.posts_to && <span className="text-white/40"> → {r.posts_to}</span>}
                    </li>
                  ))}
              </ul>
              <Button onClick={createMissing} disabled={!!busy} className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold" data-testid="qbo-create-missing-btn">
                {busy === "create-missing"
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</>
                  : <><Plus className="w-4 h-4 mr-2" />Create the {setupPreview.to_create} missing</>}
              </Button>
            </>
          )}
        </Card>
      )}

      {status?.connected && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="qbo-payout-account">
          <p className="font-medium mb-1">Where Stripe payouts land</p>
          <p className="text-sm text-white/50 mb-4">
            Sales deposit into Stripe Clearing one at a time; Stripe pays out in batches.
            Each payout is recorded as a single transfer into this account, which is what
            makes the clearing balance return to what Stripe is actually holding.
          </p>
          <select
            id="qbo-payout-account-select"
            value={payoutAccount}
            onChange={(e) => savePayoutAccount(e.target.value)}
            disabled={busy === "payout-account" || bankAccounts.length === 0}
            className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm w-full max-w-md"
            data-testid="qbo-payout-account-select"
          >
            <option value="">Choose a bank account…</option>
            {bankAccounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          {!payoutAccount && (
            <p className="text-sm text-yellow-400 mt-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Payouts won't be recorded until an account is chosen.
            </p>
          )}
        </Card>
      )}

      {status?.connected && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="qbo-sync">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
              <p className="font-medium">Sync activity</p>
              <p className="text-sm text-white/50 mt-0.5">
                Sales book themselves as they're paid. A daily job catches anything missed.
              </p>
            </div>
            {summary?.needs_attention > 0 && (
              <Badge className="bg-red-500/20 text-red-400">
                {summary.needs_attention} need{summary.needs_attention === 1 ? "s" : ""} attention
              </Badge>
            )}
          </div>

          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-white/10">
              <QboFact label="Booked" value={summary.counts?.booked || 0} />
              <QboFact label="Revenue" value={fmtMoney(summary.totals?.revenue_cents)} />
              <QboFact label="Stripe fees" value={fmtMoney(summary.totals?.fees_cents)} />
              <QboFact label="Refunded" value={fmtMoney(summary.totals?.refunds_cents)} />
            </div>
          )}

          <div className="flex gap-3 flex-wrap items-center mb-5">
            <Input
              id="qbo-backfill-days"
              value={backfillDays}
              onChange={(e) => setBackfillDays(e.target.value.replace(/\D/g, ""))}
              className="w-24 bg-white/5 border-white/10 rounded-sm"
              aria-label="Days to backfill"
            />
            <Button
              onClick={() => runSync(`backfill?days=${backfillDays || 90}`, "Backfill", {
                describe: (d) => `Backfill: ${d.booked} booked, ${d.already} already there, ${d.failed} failed`,
              })}
              disabled={!!busy}
              className="bg-white/10 hover:bg-white/20 rounded-sm"
              data-testid="qbo-backfill-btn"
            >
              {busy.startsWith("backfill") ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Backfill past days
            </Button>
            {summary?.needs_attention > 0 && (
              <Button
                onClick={() => runSync("retry-all", "Retry", {
                  describe: (d) => `Retried ${d.retried}: ${d.booked} booked, ${d.still_failing} still failing`,
                })}
                disabled={!!busy}
                className="bg-white/10 hover:bg-white/20 rounded-sm"
                data-testid="qbo-retry-btn"
              >
                {busy === "retry-all" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Retry {summary.needs_attention}
              </Button>
            )}
          </div>

          {logRows.length === 0 ? (
            <p className="text-sm text-white/40">
              Nothing synced yet. The next paid sale will appear here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[36rem]">
                <thead>
                  <tr className="text-left text-white/40 text-[11px] uppercase tracking-wider">
                    <th className="pb-2 font-medium">When</th>
                    <th className="pb-2 font-medium">What</th>
                    <th className="pb-2 font-medium text-right">Gross</th>
                    <th className="pb-2 font-medium text-right">Fee</th>
                    <th className="pb-2 font-medium text-right">Net</th>
                    <th className="pb-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((row) => (
                    <tr key={row.stripe_id} className="border-t border-white/5" data-testid="qbo-log-row">
                      <td className="py-2 text-white/50 whitespace-nowrap">{fmtWhen(row.txn_date).split(",")[0]}</td>
                      <td className="py-2">
                        <div>
                          {row.plan_type || <span className="text-white/40">no plan type</span>}
                          {row.source === "refund" && <span className="text-white/40"> (refund)</span>}
                        </div>
                        {/* The Stripe id is what you search on to work out what
                            a row actually was — without it a failed row can't
                            be matched to anything in Stripe. */}
                        <div className="text-[11px] text-white/35 font-mono mt-0.5 break-all">
                          {row.stripe_id}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">{fmtMoney(row.gross_cents)}</td>
                      <td className="py-2 text-right tabular-nums text-white/50">
                        {row.fee_cents ? `−${fmtMoney(row.fee_cents)}` : "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums">{fmtMoney(row.net_cents)}</td>
                      <td className="py-2"><QboStatus row={row} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {taxjar && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="taxjar-status">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="font-medium flex items-center gap-2">
                Sales tax tracking
                <Badge className={taxjar.environment === "production"
                  ? "bg-green-500/20 text-green-400"
                  : "bg-yellow-500/20 text-yellow-400"}>
                  {taxjar.environment}
                </Badge>
              </p>
              <p className="text-sm text-white/50 mt-1">
                Every sale is reported to TaxJar so it can watch for nexus in new states.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={loadTaxjar} className="text-white/40 hover:text-white" data-testid="taxjar-refresh">
              <RotateCcw className="w-4 h-4 mr-1.5" />Refresh
            </Button>
          </div>

          {!taxjar.configured ? (
            <p className="text-sm text-red-400 flex items-start gap-2" data-testid="taxjar-unconfigured">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              TAXJAR_API_KEY isn't set on this service — nothing is being reported.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <QboFact label="Last reported" value={fmtWhen(taxjar.last_success_at)} />
                <QboFact label="Status" value={taxjar.healthy ? "Working" : "Needs attention"} />
                {taxjar.last_error_at && (
                  <QboFact label="Last failure" value={fmtWhen(taxjar.last_error_at)} />
                )}
              </div>
              {taxjar.last_error && (
                <p className="text-sm text-red-400 mt-4 break-words" data-testid="taxjar-error">
                  {taxjar.last_error}
                </p>
              )}
              {!taxjar.last_success_at && !taxjar.last_error && (
                <p className="text-sm text-white/40 mt-4">
                  Nothing reported yet. The next paid sale will show up here.
                </p>
              )}
            </>
          )}
        </Card>
      )}

      {status?.connected && isSandbox && (
        <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="qbo-sandbox-tools">
          <p className="font-medium mb-1">Sandbox tools</p>
          <p className="text-sm text-white/50 mb-4">
            Posts a $19.00 test sale with a &minus;$0.85 fee and checks it totals $18.15.
            Refuses to run against production books.
          </p>
          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => runSandbox("smoke-test", setSmokeResult, "Smoke test")} disabled={!!busy} className="bg-white/10 hover:bg-white/20 rounded-sm" data-testid="qbo-smoke-btn">
              {busy === "smoke-test" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Post test receipt
            </Button>
          </div>

          {smokeResult && (
            <div className="mt-5 pt-5 border-t border-white/10 text-sm" data-testid="qbo-smoke-result">
              {smokeResult.ok ? (
                <p className="text-green-400">
                  Receipt {smokeResult.doc_number} totals ${smokeResult.quickbooks_total} — the negative
                  fee line was accepted and the receipt nets to the deposit.
                </p>
              ) : (
                <p className="text-red-400">
                  {smokeResult.error
                    || `QuickBooks recorded $${smokeResult.quickbooks_total} but the net should be $${smokeResult.expected_net}.`}
                </p>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function QboFact({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-white/40">{label}</p>
      <p className="text-sm mt-0.5">{value}</p>
    </div>
  );
}

function fmtWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function fmtMoney(cents) {
  if (cents === null || cents === undefined) return "—";
  const negative = cents < 0;
  const amount = (Math.abs(cents) / 100).toFixed(2);
  return `${negative ? "−" : ""}$${amount}`;
}

const QBO_STATUS_STYLES = {
  booked: "bg-green-500/20 text-green-400",
  skipped: "bg-white/10 text-white/50",
  failed: "bg-red-500/20 text-red-400",
  waiting_for_fee: "bg-yellow-500/20 text-yellow-400",
  pending: "bg-blue-500/20 text-blue-400",
};

function QboStatus({ row }) {
  const label = row.status === "waiting_for_fee" ? "awaiting fee" : row.status;
  // The reason matters more than the status for anything not booked.
  const detail = row.skip_reason || row.error;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <Badge className={`${QBO_STATUS_STYLES[row.status] || "bg-white/10 text-white/50"} w-fit`}>
        {label}
      </Badge>
      {detail && row.status !== "booked" && (
        // Wrapped rather than truncated: the reason is the whole point of
        // looking at a failed row, and it was being cut off mid-sentence.
        <span className="text-[11px] text-white/40 max-w-[22rem] whitespace-normal break-words">
          {detail}
        </span>
      )}
    </span>
  );
}

function PaymentsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("subscriptions"); // "subscriptions" | "jobs" | "members"
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleCancelRefund = async (id, withRefund, isStripeSource) => {
    const action = withRefund ? "cancel and refund" : "cancel";
    if (!confirm(`Are you sure you want to ${action} this subscription?`)) return;
    setActionLoading(id);
    try {
      const params = isStripeSource
        ? `subscription_id=${id}&refund=${withRefund}`
        : `transaction_id=${id}&refund=${withRefund}`;
      const res = await fetch(`${API}/subscriptions/admin/cancel-refund?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(withRefund ? "Subscription cancelled and refunded" : "Subscription cancelled");
        fetchData();
      } else {
        toast.error(result.detail || "Action failed");
      }
    } catch {
      toast.error("Network error");
    }
    setActionLoading(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>;
  if (!data) return <p className="text-white/50">Failed to load payment data.</p>;

  const { subscription_transactions, job_listings, plan_summary, totals } = data;

  const PLAN_LABELS = {
    second_monthly: "Second Shooter Pro (Monthly)",
    second_yearly: "Second Shooter Pro (Yearly)",
    studio_monthly: "Studio (Monthly)",
    studio_yearly: "Studio (Yearly)",
    combined_monthly: "All Access (Monthly)",
    combined_yearly: "All Access (Yearly)",
  };

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div data-testid="payments-panel">
      <h2 className="text-lg font-bold mb-2">Payments & Subscriptions</h2>
      <p className="text-white/50 text-sm mb-6">Track revenue from subscriptions and job listings.</p>

      {/* Revenue summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white/[0.03] border-white/10 p-4">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Actual Revenue</div>
          <div className="text-2xl font-bold text-green-400">${totals.subscription_revenue?.toFixed(2)}</div>
          <div className="text-xs text-white/30 mt-1">{totals.total_subscription_txns} subscriptions</div>
        </Card>
        <Card className="bg-white/[0.03] border-white/10 p-4">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Discounts Applied</div>
          <div className="text-2xl font-bold text-yellow-400">${totals.total_discount?.toFixed(2)}</div>
          <div className="text-xs text-white/30 mt-1">List price: ${totals.subscription_list_price?.toFixed(2)}</div>
        </Card>
        <Card className="bg-white/[0.03] border-white/10 p-4">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Job Listing Revenue</div>
          <div className="text-2xl font-bold text-blue-400">${totals.job_listing_revenue?.toFixed(2)}</div>
          <div className="text-xs text-white/30 mt-1">{totals.total_paid_jobs} paid jobs</div>
        </Card>
        <Card className="bg-white/[0.03] border-white/10 p-4">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Total Revenue</div>
          <div className="text-2xl font-bold">${totals.total_revenue?.toFixed(2)}</div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm"
          data-testid="payments-search"
        />
      </div>

      {/* Data source indicator */}
      {data.data_source && (
        <p className="text-xs text-white/20 mb-4">
          Data source: {data.data_source === "stripe" ? "Live Stripe invoices" : "Local database records"}
        </p>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-white/10 mb-4">
        {[
          { key: "subscriptions", label: "Subscription Payments" },
          { key: "jobs", label: "Job Listing Payments" },
          { key: "members", label: "Members by Plan" },
          { key: "gift", label: "Gift Free Months" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === t.key ? "border-[#FF6B6B] text-white" : "border-transparent text-white/50 hover:text-white/70"
            }`}
            data-testid={`payments-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Subscription Payments Table */}
      {view === "subscriptions" && (
        <div className="overflow-x-auto">
          {(() => {
            const q = searchQuery.toLowerCase();
            const filtered = q ? subscription_transactions.filter(t =>
              (t.user_name || "").toLowerCase().includes(q) || (t.user_email || "").toLowerCase().includes(q)
            ) : subscription_transactions;
            return (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/40 text-xs uppercase">
                <th className="py-2 px-3 text-left">Date</th>
                <th className="py-2 px-3 text-left">User</th>
                <th className="py-2 px-3 text-left">Plan</th>
                <th className="py-2 px-3 text-right">Amount</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-white/30">{q ? "No matching payments." : "No subscription payments yet."}</td></tr>
              )}
              {filtered.map((t, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2.5 px-3 text-white/50">{formatDate(t.created_at)}</td>
                  <td className="py-2.5 px-3">
                    <div className="text-white/80">{t.user_name}</div>
                    <div className="text-white/30 text-xs">{t.user_email}</div>
                  </td>
                  <td className="py-2.5 px-3 text-white/60">{PLAN_LABELS[t.plan_type] || t.plan_type}</td>
                  <td className="py-2.5 px-3 text-right">
                    {t.discount_display ? (
                      <div>
                        <span className="font-bold text-green-400">${t.actual_amount?.toFixed(2)}</span>
                        <div className="text-xs text-white/30 line-through">${t.list_price?.toFixed(2)}</div>
                        <div className="text-xs text-yellow-400">{t.discount_display}</div>
                      </div>
                    ) : (
                      <span className="font-bold text-green-400">${t.actual_amount?.toFixed(2)}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${
                      t.status === "paid" ? "bg-green-500/20 text-green-400" :
                      t.status === "completed" || t.status === "active" ? "bg-green-500/20 text-green-400" :
                      t.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                      t.status === "refunded" ? "bg-purple-500/20 text-purple-400" :
                      "bg-red-500/20 text-red-400"
                    }`}>{t.status}</span>
                    {(t.sub_status === "canceled" || t.cancel_at_period_end) && (
                      <div className="text-xs text-yellow-400 mt-1">Subscription cancelled</div>
                    )}
                    {t.cancel_at_period_end && t.subscription_period_end && (
                      <div className="text-xs text-white/40 mt-0.5">Active until {formatDate(t.subscription_period_end)}</div>
                    )}
                    {t.sub_status === "active" && !t.cancel_at_period_end && t.subscription_period_end && (
                      <div className="text-xs text-white/30 mt-0.5">Renews {formatDate(t.subscription_period_end)}</div>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {!t.cancel_at_period_end && t.sub_status !== "canceled" && (t.status === "paid" || t.status === "completed" || t.status === "active" || t.status === "pending") && (
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => {
                            if (!confirm("Cancel subscription for " + (t.user_name || "this user") + "? They keep access until end of billing period.")) return;
                            var params = t.stripe_subscription_id
                              ? "subscription_id=" + t.stripe_subscription_id + "&refund=false"
                              : "user_id=" + t.user_id + "&refund=false";
                            (async () => {
                              setActionLoading(t.invoice_id);
                              try {
                                var res = await fetch(API + "/subscriptions/admin/cancel-refund?" + params, {
                                  method: "POST", headers: { Authorization: "Bearer " + token },
                                });
                                var d = await res.json();
                                if (res.ok) { toast.success("Subscription cancelled at period end"); fetchData(); }
                                else { toast.error(d.detail || "Failed to cancel"); }
                              } catch (e) { toast.error("Network error"); }
                              setActionLoading(null);
                            })();
                          }}
                          disabled={actionLoading}
                          className="text-xs px-2 py-1 text-yellow-400 hover:bg-yellow-500/10 rounded-sm border border-yellow-500/20"
                          data-testid={"cancel-" + t.invoice_id}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            if (!confirm("Cancel AND refund " + (t.user_name || "this user") + "? This immediately cancels and refunds the last payment.")) return;
                            var params = t.stripe_subscription_id
                              ? "subscription_id=" + t.stripe_subscription_id + "&refund=true"
                              : "user_id=" + t.user_id + "&refund=true";
                            (async () => {
                              setActionLoading(t.invoice_id);
                              try {
                                var res = await fetch(API + "/subscriptions/admin/cancel-refund?" + params, {
                                  method: "POST", headers: { Authorization: "Bearer " + token },
                                });
                                var d = await res.json();
                                if (res.ok) {
                                  toast.success(d.refunded ? "Cancelled and refunded" : "Cancelled (refund may need manual processing in Stripe)");
                                  if (d.cancel_error) toast.error("Cancel note: " + d.cancel_error);
                                  if (d.refund_error) toast.error("Refund note: " + d.refund_error);
                                  fetchData();
                                } else { toast.error(d.detail || "Failed"); }
                              } catch (e) { toast.error("Network error"); }
                              setActionLoading(null);
                            })();
                          }}
                          disabled={actionLoading}
                          className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded-sm border border-red-500/20"
                          data-testid={"refund-" + t.invoice_id}
                        >
                          Cancel &amp; Refund
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            );
          })()}
        </div>
      )}

      {/* Job Listing Payments Table */}
      {view === "jobs" && (
        <div className="overflow-x-auto">
          {(() => {
            const q = searchQuery.toLowerCase();
            const filteredJobs = q ? job_listings.filter(j =>
              (j.studio_name || "").toLowerCase().includes(q) || (j.studio_email || "").toLowerCase().includes(q) || (j.title || "").toLowerCase().includes(q)
            ) : job_listings;
            return (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/40 text-xs uppercase">
                <th className="py-2 px-3 text-left">Date</th>
                <th className="py-2 px-3 text-left">Studio</th>
                <th className="py-2 px-3 text-left">Job Title</th>
                <th className="py-2 px-3 text-right">Amount</th>
                <th className="py-2 px-3 text-left">Type</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-white/30">{q ? "No matching jobs." : "No paid job listings yet."}</td></tr>
              )}
              {filteredJobs.map((j, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2.5 px-3 text-white/50">{formatDate(j.created_at)}</td>
                  <td className="py-2.5 px-3">
                    <div className="text-white/80">{j.studio_name}</div>
                    <div className="text-white/30 text-xs">{j.studio_email}</div>
                  </td>
                  <td className="py-2.5 px-3 text-white/60">{j.title}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-blue-400">
                    {/* Was `amount_paid || 10`, which drew $10.00 against every
                        listing that cost nothing — a discounted or auto-free job
                        read as revenue that never arrived. */}
                    {j.amount_paid > 0
                      ? `$${j.amount_paid.toFixed(2)}`
                      : <span className="text-white/30">$0.00</span>}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${
                      j.auto_free || !(j.amount_paid > 0)
                        ? "bg-white/5 text-white/30"
                        : "bg-blue-500/20 text-blue-400"
                    }`}>
                      {j.auto_free
                        ? "Auto-free"
                        : j.first_listing_free
                          ? "First listing free"
                          : j.listing_gifted
                            ? "Gifted"
                            : j.discount_code
                              ? "Code " + j.discount_code
                              : j.amount_paid > 0 ? "Paid" : "No charge"}
                    </span>
                    {/* Only flag a listing that was paid for and never went
                        live. Filled, expired and completed jobs have simply run
                        their course — badging those would bury the real ones. */}
                    {j.status === "pending_payment" && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-300/80">
                        never went live
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            );
          })()}
        </div>
      )}

      {/* Members by Plan */}
      {view === "members" && (
        <div className="space-y-6">
          {Object.keys(plan_summary).length === 0 && (
            <p className="text-white/30 text-center py-8">No subscription members yet.</p>
          )}
          {["second_monthly", "second_yearly", "combined_monthly", "combined_yearly", "studio_monthly", "studio_yearly"].map(planKey => {
            let members = plan_summary[planKey];
            if (!members || members.length === 0) return null;
            const q = searchQuery.toLowerCase();
            if (q) {
              members = members.filter(m =>
                (m.user_name || "").toLowerCase().includes(q) || (m.user_email || "").toLowerCase().includes(q)
              );
              if (members.length === 0) return null;
            }
            return (
              <Card key={planKey} className="bg-white/[0.03] border-white/10 p-5" data-testid={`plan-${planKey}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">{PLAN_LABELS[planKey] || planKey}</h3>
                  <span className="text-xs bg-white/10 px-2 py-0.5 rounded-sm">{members.length} member{members.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-1">
                  {members.map((m, i) => {
                    const txn = subscription_transactions.find(t => t.user_email === m.user_email && t.stripe_subscription_id);
                    const isCancelled = txn && (txn.cancel_at_period_end || txn.sub_status === "canceled");
                    const cancelId = txn?.stripe_subscription_id || m.user_id;
                    const canCancel = !isCancelled && (txn?.stripe_subscription_id || m.user_id);
                    return (
                    <div key={i} className="flex items-center justify-between py-2 text-sm border-b border-white/5 last:border-0">
                      <div>
                        <span className="text-white/80">{m.user_name}</span>
                        <span className="text-white/30 text-xs ml-2">{m.user_email}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {m.discount_display ? (
                          <div className="text-right">
                            <span className="text-green-400 font-medium">${m.actual_amount?.toFixed(2)}</span>
                            <span className="text-white/20 text-xs line-through ml-1">${m.list_price?.toFixed(2)}</span>
                          </div>
                        ) : (
                          <span className="text-green-400 font-medium">${m.actual_amount?.toFixed(2)}</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          m.status === "completed" || m.status === "active" || m.status === "paid" ? "bg-green-500/20 text-green-400" :
                          m.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-red-500/20 text-red-400"
                        }`}>{m.status}</span>
                        {isCancelled ? (
                          <span className="text-xs text-yellow-400">
                            Cancelled{txn.subscription_period_end ? ` (until ${formatDate(txn.subscription_period_end)})` : ""}
                          </span>
                        ) : canCancel ? (
                          <button
                            onClick={async () => {
                              if (!confirm(`Cancel subscription for ${m.user_name}?`)) return;
                              try {
                                const endpoint = txn?.stripe_subscription_id
                                  ? `${API}/subscriptions/admin/cancel-refund?subscription_id=${txn.stripe_subscription_id}&refund=false`
                                  : `${API}/admin/users/${m.user_id}/cancel-subscription`;
                                const res = await fetch(endpoint, {
                                  method: "POST",
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                if (res.ok) {
                                  toast.success(`Subscription cancelled for ${m.user_name}`);
                                  fetchData();
                                } else {
                                  const d = await res.json();
                                  toast.error(d.detail || "Failed to cancel");
                                }
                              } catch { toast.error("Network error"); }
                            }}
                            disabled={actionLoading}
                            className="text-xs px-2 py-1 text-yellow-400 hover:bg-yellow-500/10 rounded-sm border border-yellow-500/20"
                          >
                            Cancel
                          </button>
                        ) : null}
                        <span className="text-white/20 text-xs">{formatDate(m.created_at)}</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Gift Free Months */}
      {view === "gift" && (
        <GiftPanel token={token} />
      )}
    </div>
  );
}


function GiftPanel({ token }) {
  const [userSearch, setUserSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [months, setMonths] = useState(6);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Search users as admin types
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) { setUsers([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/admin/users?limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const q = userSearch.toLowerCase();
          const filtered = data.users.filter(u =>
            (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
          ).slice(0, 10);
          setUsers(filtered);
        }
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, token]);

  const handleGift = async () => {
    if (!selectedUser) return toast.error("Select a user first");
    if (!months || months < 1) return toast.error("Enter number of months");
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/gift-subscription`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          months_free: months,
          reason: reason,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        toast.success(`Gifted ${months} free months to ${selectedUser.name}`);
      } else {
        toast.error(data.detail || "Failed to gift");
      }
    } catch {
      toast.error("Network error");
    }
    setSending(false);
  };

  return (
    <div data-testid="gift-panel">
      <Card className="bg-white/[0.03] border-white/10 p-6">
        <h3 className="font-semibold mb-1">Gift Free Subscription Months</h3>
        <p className="text-white/50 text-sm mb-6">
          Apply free months to a user's subscription. If they have an active Stripe subscription, a 100% discount coupon will be applied automatically.
        </p>

        <div className="space-y-4">
          {/* User search */}
          <div>
            <label className="text-sm text-white/60 block mb-1.5">Find User</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setSelectedUser(null); setResult(null); }}
                placeholder="Search by name or email..."
                className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm"
                data-testid="gift-user-search"
              />
            </div>
            {users.length > 0 && !selectedUser && (
              <div className="mt-1 border border-white/10 rounded-sm bg-[#111] max-h-48 overflow-y-auto">
                {users.map(u => (
                  <button
                    key={u.user_id}
                    onClick={() => { setSelectedUser(u); setUserSearch(""); setUsers([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-white/[0.05] flex items-center justify-between text-sm"
                  >
                    <span className="text-white/80">{u.name}</span>
                    <span className="text-white/30 text-xs">{u.email}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedUser && (
              <div className="mt-2 flex items-center gap-3 p-3 bg-white/[0.05] border border-white/10 rounded-sm">
                <div className="flex-1">
                  <span className="text-white/80 font-medium">{selectedUser.name}</span>
                  <span className="text-white/30 text-xs ml-2">{selectedUser.email}</span>
                </div>
                <button onClick={() => { setSelectedUser(null); setResult(null); }} className="text-white/30 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Months */}
          <div>
            <label className="text-sm text-white/60 block mb-1.5">Free Months</label>
            <div className="flex gap-2">
              {[1, 3, 6, 12].map(m => (
                <button
                  key={m}
                  onClick={() => setMonths(m)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${
                    months === m
                      ? "bg-white text-black border-white"
                      : "bg-white/[0.05] text-white/60 border-white/10 hover:border-white/20"
                  }`}
                  data-testid={`gift-months-${m}`}
                >
                  {m} month{m !== 1 ? "s" : ""}
                </button>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="text-sm text-white/60 block mb-1.5">Reason (optional)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., No jobs in her area yet"
              className="bg-white/[0.05] border-white/10 text-white h-10 rounded-sm"
              data-testid="gift-reason"
            />
          </div>

          {/* Submit */}
          <Button
            onClick={handleGift}
            disabled={sending || !selectedUser}
            className="bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold px-6"
            data-testid="gift-submit-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
            {sending ? "Applying..." : `Gift ${months} Free Month${months !== 1 ? "s" : ""}`}
          </Button>
        </div>

        {/* Result */}
        {result && (
          <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-sm" data-testid="gift-result">
            <p className="text-green-400 font-medium mb-1">
              {result.months_free} free month{result.months_free !== 1 ? "s" : ""} gifted to {result.user_name}
            </p>
            <p className="text-white/50 text-xs">
              Stripe coupon applied: {result.stripe_applied ? "Yes" : "No (no active Stripe subscription found)"}
              {result.stripe_error && ` — Error: ${result.stripe_error}`}
            </p>
            <p className="text-white/50 text-xs">Notification email sent to {result.user_email}</p>
          </div>
        )}
      </Card>
    </div>
  );
}

function ProfileEmailsPanel({ token, stats }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("janet@studiolifesaver.com");
  const [emailLog, setEmailLog] = useState(null);
  const [loadingLog, setLoadingLog] = useState(true);
  const [logFilter, setLogFilter] = useState("all");

  useEffect(function() {
    fetchEmailLog();
  }, []);

  async function fetchEmailLog() {
    setLoadingLog(true);
    try {
      var res = await fetch(API + "/admin/email-log?limit=200", {
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        var data = await res.json();
        setEmailLog(data);
      }
    } catch (e) {
      console.error("Failed to fetch email log:", e);
    } finally {
      setLoadingLog(false);
    }
  }

  const sendSamples = async () => {
    if (!recipientEmail) return toast.error("Enter a recipient email");
    setSending(true);
    try {
      const res = await fetch(API + "/admin/profile-emails/send-samples", {
        method: "POST",
        headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_email: recipientEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        toast.success("Sample emails sent to " + recipientEmail);
        fetchEmailLog();
      } else {
        toast.error(data.detail || "Failed to send samples");
      }
    } catch(e) {
      toast.error("Network error");
    } finally {
      setSending(false);
    }
  };

  // Combine drip history and email_log into one unified list
  var allEmails = [];
  if (emailLog) {
    // Add drip history entries
    (emailLog.drip_history || []).forEach(function(d) {
      allEmails.push({
        to: d.user_email || d.email || "—",
        subject: d.email_type === "missing_location" ? "Missing Location Drip" : d.email_type === "incomplete_profile" ? "Incomplete Profile Drip" : (d.email_type || "Drip Email"),
        success: d.success,
        sent_at: d.sent_at,
        source: "drip",
        user_id: d.user_id,
      });
    });
    // Add general email_log entries
    (emailLog.email_log || []).forEach(function(e) {
      allEmails.push({
        to: e.to || "—",
        subject: e.subject || "—",
        success: e.success,
        sent_at: e.sent_at,
        source: "general",
        status_detail: e.status_detail,
      });
    });
  }

  // Deduplicate by to+sent_at (drip and general may overlap)
  var seen = new Set();
  allEmails = allEmails.filter(function(e) {
    var key = e.to + "|" + (e.sent_at || "").substring(0, 19);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Sort by date descending
  allEmails.sort(function(a, b) {
    return (b.sent_at || "").localeCompare(a.sent_at || "");
  });

  // Filter
  var filteredEmails = allEmails;
  if (logFilter === "drip") filteredEmails = allEmails.filter(function(e) { return e.source === "drip"; });
  if (logFilter === "general") filteredEmails = allEmails.filter(function(e) { return e.source === "general"; });
  if (logFilter === "failed") filteredEmails = allEmails.filter(function(e) { return !e.success; });

  function formatDate(iso) {
    if (!iso) return "—";
    try {
      var d = new Date(iso);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    } catch(e) { return iso; }
  }

  var dripStats = emailLog ? emailLog.drip_stats : null;
  var logStats = emailLog ? emailLog.log_stats : null;

  return (
    <div data-testid="emails-panel">
      <h2 className="text-lg font-bold mb-2">Email Log</h2>
      <p className="text-white/50 text-sm mb-6">
        View all emails sent from the platform, including profile completion drip emails and transactional emails.
      </p>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white/[0.03] border-white/10 p-4" data-testid="stat-drip-total">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Drip Emails Sent</div>
          <div className="text-2xl font-bold">{dripStats ? dripStats.total_sent : "—"}</div>
          <div className="text-white/30 text-xs mt-2 pt-1 border-t border-white/5">
            {dripStats ? dripStats.missing_location_sent + " location · " + dripStats.incomplete_profile_sent + " profile" : ""}
          </div>
        </Card>
        <Card className="bg-green-500/5 border-green-500/20 p-4" data-testid="stat-success">
          <div className="text-green-400/60 text-xs uppercase tracking-wider mb-1">Successful</div>
          <div className="text-2xl font-bold text-green-400">{dripStats ? dripStats.successful : "—"}</div>
        </Card>
        <Card className="bg-red-500/5 border-red-500/20 p-4" data-testid="stat-failed">
          <div className="text-red-400/60 text-xs uppercase tracking-wider mb-1">Failed</div>
          <div className="text-2xl font-bold text-red-400">{dripStats ? dripStats.failed : "0"}</div>
        </Card>
        <Card className="bg-white/[0.03] border-white/10 p-4" data-testid="stat-recent">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Last 7 Days</div>
          <div className="text-2xl font-bold">
            {dripStats ? (dripStats.last_7_days.missing_location + dripStats.last_7_days.incomplete_profile) : "—"}
          </div>
          <div className="text-white/30 text-xs mt-1">drip emails</div>
        </Card>
      </div>

      {/* Email type descriptions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="bg-white/[0.03] border-white/10 p-5">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-red-400" />
            <span className="font-semibold text-sm">Missing Location Email</span>
          </div>
          <p className="text-white/50 text-sm leading-relaxed">
            Sent to users who have no city/state on their profile. Explains that they are invisible
            to studios searching by location and will not be matched to nearby jobs.
          </p>
        </Card>
        <Card className="bg-white/[0.03] border-white/10 p-5">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-yellow-400" />
            <span className="font-semibold text-sm">Incomplete Profile Email</span>
          </div>
          <p className="text-white/50 text-sm leading-relaxed">
            Sent to users below 60% profile completion. Lists specifically what is missing
            (portfolio, bio, rate, etc.) and why each field matters for getting hired.
          </p>
        </Card>
      </div>

      {/* Email Log Table */}
      <Card className="bg-white/[0.03] border-white/10 p-5 mb-6" data-testid="email-log-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold">Email History</h3>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-white/[0.03] rounded-sm p-0.5">
              {[
                { key: "all", label: "All" },
                { key: "drip", label: "Drip" },
                { key: "general", label: "General" },
                { key: "failed", label: "Failed" },
              ].map(function(f) {
                return (
                  <button
                    key={f.key}
                    onClick={function() { setLogFilter(f.key); }}
                    className={"px-3 py-1 text-xs font-medium rounded-sm transition-colors " + (logFilter === f.key ? "bg-white/10 text-white" : "text-white/40 hover:text-white/60")}
                    data-testid={"email-filter-" + f.key}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            <Button variant="ghost" size="sm" onClick={fetchEmailLog} className="text-white/50 hover:text-white h-7 px-2" data-testid="refresh-log-btn">
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {loadingLog ? (
          <div className="text-center py-8 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading email log...
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="text-center py-8 text-white/40">
            <Mail className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>No emails found{logFilter !== "all" ? " for this filter" : ""}.</p>
            <p className="text-xs mt-1">Emails will appear here once the drip system runs or transactional emails are sent.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-log-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Status</th>
                  <th className="text-left py-2 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">To</th>
                  <th className="text-left py-2 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Subject / Type</th>
                  <th className="text-left py-2 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Source</th>
                  <th className="text-left py-2 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmails.map(function(email, i) {
                  return (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-2.5 px-2">
                        {email.success ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-400" />
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-white/70 max-w-[200px] truncate">{email.to}</td>
                      <td className="py-2.5 px-2 text-white/60 max-w-[280px] truncate">{email.subject}</td>
                      <td className="py-2.5 px-2">
                        <Badge className={"text-xs rounded-sm " + (email.source === "drip" ? "bg-purple-500/15 text-purple-400 border-purple-500/30" : "bg-blue-500/15 text-blue-400 border-blue-500/30")}>
                          {email.source === "drip" ? "Drip" : "Transactional"}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-2 text-white/40 text-xs whitespace-nowrap">{formatDate(email.sent_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-white/30 text-xs mt-3">{filteredEmails.length} email{filteredEmails.length !== 1 ? "s" : ""} shown</p>
          </div>
        )}
      </Card>

      {/* Send samples section */}
      <Card className="bg-white/[0.03] border-white/10 p-5" data-testid="send-samples-card">
        <h3 className="font-semibold mb-3">Send Sample Emails</h3>
        <p className="text-white/50 text-sm mb-4">
          Both drip email templates will be sent to the address below for your review.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="recipient@example.com"
              className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm"
              data-testid="sample-email-input"
            />
          </div>
          <Button
            onClick={sendSamples}
            disabled={sending || !recipientEmail}
            className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-10 px-6"
            data-testid="send-samples-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            {sending ? "Sending..." : "Send Samples"}
          </Button>
        </div>
      </Card>

      {/* Sample results */}
      {result && (
        <Card className="bg-white/[0.03] border-white/10 p-5 mt-4" data-testid="sample-results">
          <h3 className="font-semibold mb-3 text-green-400">Samples Sent</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="text-sm">
              <span className="text-white/40">Users missing location: </span>
              <span className="font-bold text-red-400">{result.summary?.users_missing_location}</span>
            </div>
            <div className="text-sm">
              <span className="text-white/40">Users below 60%: </span>
              <span className="font-bold text-yellow-400">{result.summary?.users_below_60_pct}</span>
            </div>
            <div className="text-sm">
              <span className="text-white/40">Total users: </span>
              <span className="font-bold">{result.summary?.total_users}</span>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <span className={result.results?.missing_location ? "text-green-400" : "text-red-400"}>
              Missing Location: {result.results?.missing_location ? "Sent" : "Failed"}
            </span>
            <span className={result.results?.incomplete_profile ? "text-green-400" : "text-red-400"}>
              Incomplete Profile: {result.results?.incomplete_profile ? "Sent" : "Failed"}
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}



/* ── Users Table ──────────────────────────────── */
/**
 * Photographer of the Week submissions.
 *
 * The images are the point of the feature, so they are shown as a thumbnail
 * grid rather than as links, and every submission can be pulled down as a
 * single zip instead of saved one file at a time.
 */
/**
 * Review-request funnel.
 *
 * The number that matters is how many people have never asked for a review —
 * that is both the problem and the audience for fixing it, so the table
 * defaults to exactly those users and exports as the campaign list.
 */
function ReviewFunnelPanel({ funnel }) {
  const [filter, setFilter] = useState("never");
  const [search, setSearch] = useState("");

  if (!funnel) {
    return (
      <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">
        Loading review data…
      </Card>
    );
  }

  const s = funnel.summary;
  const pct = function (n, of) { return of ? Math.round((n / of) * 100) : 0; };

  const rows = (funnel.users || []).filter(function (u) {
    if (filter === "never" && u.requests_sent > 0) return false;
    if (filter === "asked" && u.requests_sent === 0) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        (u.name || "").toLowerCase().indexOf(q) === -1 &&
        (u.email || "").toLowerCase().indexOf(q) === -1 &&
        (u.location || "").toLowerCase().indexOf(q) === -1
      ) return false;
    }
    return true;
  });

  const exportCsv = function () {
    const header = ["Name", "Email", "Roles", "Location", "Profile %", "Requests sent", "Reviews received", "Opted out"];
    const body = rows.map(function (u) {
      return [u.name, u.email, (u.roles || []).join(" "), u.location, u.profile_percent,
              u.requests_sent, u.reviews_received, u.announcements_opted_out ? "yes" : "no"];
    });
    const csv = [header].concat(body)
      .map(function (r) {
        return r.map(function (c) { return '"' + String(c == null ? "" : c).replace(/"/g, '""') + '"'; }).join(",");
      })
      .join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "review-funnel-" + filter + ".csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="review-funnel-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Never asked for a review"
          value={s.never_requested + " of " + s.active_users}
        />
        <StatCard
          icon={<Star className="w-5 h-5" />}
          label="Requests sent"
          value={s.requests_sent}
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          label="Reviews left"
          value={s.requests_completed + " (" + pct(s.requests_completed, s.requests_sent) + "%)"}
        />
        <StatCard
          icon={<ExternalLink className="w-5 h-5" />}
          label="Non-members reached"
          value={s.requests_to_non_members}
        />
      </div>

      {/* The acquisition question: does asking a studio for a review bring them in? */}
      <Card className="bg-white/[0.03] border-white/10 p-5 mb-6">
        <div className="flex items-center gap-2 text-white/40 mb-3">
          <BarChart3 className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wider font-medium">Does asking bring studios in?</span>
        </div>
        <p className="text-sm text-white/60">
          <span className="text-white font-bold text-lg">{s.non_members_joined_after}</span> of{" "}
          {s.requests_to_non_members} people asked for a review who were not members have since
          registered.{" "}
          {s.non_member_requests_completed > 0 && (
            <>{s.non_member_requests_completed} of those requests got a review written.</>
          )}
        </p>
        {(funnel.joined_after_request || []).length > 0 && (
          <ul className="mt-3 space-y-1">
            {funnel.joined_after_request.map(function (j) {
              return (
                <li key={j.email} className="text-sm text-white/60">
                  <span className="text-white/85">{j.name || j.email}</span>
                  <span className="text-white/30"> — asked by {j.asked_by}</span>
                  {j.review_left && <span className="text-green-400/70"> · left a review</span>}
                </li>
              );
            })}
          </ul>
        )}
        <p className="text-sm text-white/50 mt-3 pt-3 border-t border-white/[0.07]">
          Sent by the requester themselves: <span className="text-white">{s.requests_self_sent}</span>
          {" · sent by us: "}<span className="text-white">{s.requests_platform_sent}</span>
          {s.requests_platform_sent > 0 && (
            <>
              {", of which "}
              <span className="text-white">{s.request_emails_logged}</span> have a delivery record
              {s.request_emails_failed > 0 && (
                <span className="text-red-400"> and {s.request_emails_failed} failed</span>
              )}
            </>
          )}
        </p>
        {s.requests_platform_sent > s.request_emails_logged + s.request_emails_failed && (
          <p className="text-xs text-white/30 mt-1">
            Requests with no delivery record predate outcome tracking — that means unknown, not failed.
          </p>
        )}

        {s.opted_out_of_announcements > 0 && (
          <p className="text-xs text-amber-400/80 mt-3">
            {s.opted_out_of_announcements} user(s) opted out of announcements — exclude them from any
            broadcast.
          </p>
        )}
      </Card>

      {(funnel.requests || []).length > 0 && (
        <Card className="bg-white/[0.03] border-white/10 p-5 mb-6">
          <div className="flex items-center gap-2 text-white/40 mb-4">
            <Send className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider font-medium">
              Every request ({funnel.requests.length})
            </span>
          </div>
          <div className="hidden md:grid grid-cols-12 gap-3 px-1 pb-2 text-[11px] text-white/35 uppercase tracking-wider">
            <div className="col-span-3">Asked by</div>
            <div className="col-span-3">Asked</div>
            <div className="col-span-2">How</div>
            <div className="col-span-2">Delivery</div>
            <div className="col-span-2">Result</div>
          </div>
          <div className="space-y-1">
            {funnel.requests.map(function (r) {
              return (
                <div
                  key={r.request_id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center text-sm px-1 py-2 border-t border-white/[0.06]"
                  data-testid={"review-request-" + r.request_id}
                >
                  <div className="col-span-3 truncate">{r.requester_name || "—"}</div>
                  <div className="col-span-3 truncate text-white/50">
                    {r.recipient_email}
                    {r.recipient_is_member && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-white/30">member</span>
                    )}
                  </div>
                  <div className="col-span-2 text-white/50">
                    {r.delivery === "self" ? "Sent themselves" : "Sent by us"}
                  </div>
                  <div className="col-span-2">
                    {r.delivery === "self" ? (
                      <span className="text-white/25">n/a</span>
                    ) : r.email_sent === true ? (
                      <span className="text-green-400/80">accepted</span>
                    ) : r.email_sent === false ? (
                      <span className="text-red-400">failed</span>
                    ) : (
                      <span className="text-white/30">unknown</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    {r.review_rating ? (
                      <span className="text-green-400">{r.review_rating}★ review</span>
                    ) : (
                      <span className="text-white/30">no response</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <select
          value={filter}
          onChange={function (e) { setFilter(e.target.value); }}
          className="bg-white/[0.05] border border-white/10 text-white h-10 rounded-sm px-3 text-sm"
          data-testid="review-funnel-filter"
        >
          <option value="never">Never asked ({s.never_requested})</option>
          <option value="asked">Has asked ({s.requesters})</option>
          <option value="all">All users ({s.active_users})</option>
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={search}
            onChange={function (e) { setSearch(e.target.value); }}
            placeholder="Search by name, email, or location..."
            className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm"
          />
        </div>
        <Button
          onClick={exportCsv}
          className="bg-white text-black hover:bg-white/90"
          data-testid="review-funnel-export"
        >
          <Download className="w-4 h-4 mr-2" />Export list ({rows.length})
        </Button>
      </div>

      <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-xs text-white/40 uppercase tracking-wider">
        <div className="col-span-3">User</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-2">Location</div>
        <div className="col-span-1">Profile</div>
        <div className="col-span-1">Asked</div>
        <div className="col-span-1">Reviews</div>
        <div className="col-span-1"></div>
      </div>

      <div className="space-y-2">
        {rows.map(function (u) {
          return (
            <Card key={u.user_id} className="bg-white/[0.03] border-white/10 px-4 py-3">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center text-sm">
                <div className="col-span-3 truncate">
                  {u.name || <span className="text-white/30">No name</span>}
                  {u.announcements_opted_out && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400/80">opted out</span>
                  )}
                </div>
                <div className="col-span-3 truncate text-white/50">{u.email}</div>
                <div className="col-span-2 truncate text-white/50">
                  {u.location || <span className="text-white/25">—</span>}
                </div>
                <div className={"col-span-1 " + (u.profile_percent < 60 ? "text-amber-400" : "text-white/60")}>
                  {u.profile_percent}%
                </div>
                <div className={"col-span-1 " + (u.requests_sent ? "text-white" : "text-white/25")}>
                  {u.requests_sent}
                </div>
                <div className={"col-span-1 " + (u.reviews_received ? "text-white" : "text-white/25")}>
                  {u.reviews_received}
                </div>
                <div className="col-span-1 text-right">
                  <a href={"/profile/" + u.user_id} target="_blank" rel="noreferrer" className="text-white/30 hover:text-white">
                    <ExternalLink className="w-4 h-4 inline" />
                  </a>
                </div>
              </div>
            </Card>
          );
        })}
        {rows.length === 0 && (
          <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">
            No users match.
          </Card>
        )}
      </div>
    </div>
  );
}


function PotwPanel({ submissions, token, onRefresh }) {
  const [kindFilter, setKindFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const shown = submissions.filter(function (s) {
    return kindFilter === "all" || s.kind === kindFilter;
  });

  // Downloads need the auth header, so they go through fetch rather than a
  // plain link, then get handed to the browser as a blob.
  const download = async function (path, fallbackName, id) {
    setBusyId(id || path);
    try {
      const res = await fetch(API + path, { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) {
        const detail = await res.json().catch(function () { return {}; });
        throw new Error(detail.detail || "Download failed");
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Could not download");
    } finally {
      setBusyId(null);
    }
  };

  if (submissions.length === 0) {
    return (
      <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40" data-testid="potw-empty">
        No Photographer of the Week submissions yet.
      </Card>
    );
  }

  return (
    <div data-testid="potw-panel">
      <div className="flex flex-col sm:flex-row gap-3 mb-6 items-start sm:items-center">
        <select
          value={kindFilter}
          onChange={function (e) { setKindFilter(e.target.value); }}
          className="bg-white/[0.05] border border-white/10 text-white h-10 rounded-sm px-3 text-sm"
          data-testid="potw-kind-filter"
        >
          <option value="all">All forms ({submissions.length})</option>
          <option value="second-shooter">Second shooters</option>
          <option value="studio">Studios</option>
        </select>
        <div className="flex-1" />
        <Button
          onClick={function () {
            download(
              "/admin/potw/export.csv" + (kindFilter === "all" ? "" : "?kind=" + kindFilter),
              "photographer-of-the-week.csv",
              "csv"
            );
          }}
          disabled={busyId === "csv"}
          className="bg-white text-black hover:bg-white/90"
          data-testid="potw-export-csv"
        >
          <Download className="w-4 h-4 mr-2" />
          {busyId === "csv" ? "Preparing…" : "Export answers (CSV)"}
        </Button>
      </div>

      <div className="space-y-3">
        {shown.map(function (s) {
          const member = s.member || {};
          const allImages = Object.keys(s.images || {}).reduce(function (acc, fieldId) {
            return acc.concat((s.images[fieldId] || []).map(function (img) {
              return Object.assign({ field: fieldId }, img);
            }));
          }, []);
          const isOpen = openId === s.submission_id;
          const incomplete = (member.profile_percent || 0) < 80;

          return (
            <Card
              key={s.submission_id}
              className="bg-white/[0.03] border-white/10 p-5"
              data-testid={"potw-submission-" + s.submission_id}
            >
              <div className="flex flex-wrap items-start gap-4 mb-4">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{member.name || s.answers?.name_pronouns || "Unknown"}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-white/10 text-white/60">
                      {s.form_name}
                    </span>
                    {!s.emailed && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-500/15 text-amber-400" title="The notification email did not send — the submission is safe here">
                        email failed
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/50 mt-1">{member.email}</p>
                  <p className="text-xs text-white/30 mt-1">
                    {member.location || "No location"} ·{" "}
                    <span className={incomplete ? "text-amber-400" : "text-white/30"}>
                      profile {member.profile_percent}% complete
                    </span>
                    {s.created_at ? " · " + new Date(s.created_at).toLocaleDateString() : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  {member.user_id && (
                    <a href={"/profile/" + member.user_id} target="_blank" rel="noreferrer">
                      <Button variant="outline" className="border-white/15 bg-transparent hover:bg-white/10 text-white">
                        <ExternalLink className="w-4 h-4 mr-2" />Profile
                      </Button>
                    </a>
                  )}
                  {allImages.length > 0 && (
                    <Button
                      onClick={function () {
                        download(
                          "/admin/potw/submissions/" + s.submission_id + "/images.zip",
                          s.submission_id + ".zip",
                          s.submission_id
                        );
                      }}
                      disabled={busyId === s.submission_id}
                      className="bg-white text-black hover:bg-white/90"
                      data-testid={"potw-download-" + s.submission_id}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {busyId === s.submission_id ? "Zipping…" : "Images (" + allImages.length + ")"}
                    </Button>
                  )}
                </div>
              </div>

              {allImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid={"potw-thumbs-" + s.submission_id}>
                  {allImages.map(function (img, i) {
                    return (
                      <a
                        key={img.key}
                        href={img.url}
                        target="_blank"
                        rel="noreferrer"
                        title={img.field === "headshot" ? "Photo of them" : "Work sample"}
                      >
                        <img
                          src={img.thumb_url || img.url}
                          alt={img.field === "headshot" ? "Photo of the submitter" : "Submitted work sample " + i}
                          className="w-24 h-24 object-cover rounded-sm border border-white/10 hover:border-white/40 transition-colors"
                          loading="lazy"
                        />
                      </a>
                    );
                  })}
                </div>
              )}

              <button
                onClick={function () { setOpenId(isOpen ? null : s.submission_id); }}
                className="text-xs text-[#FF6B6B] hover:text-[#ff8585] transition-colors"
                data-testid={"potw-toggle-" + s.submission_id}
              >
                {isOpen ? "Hide answers" : "Show answers"}
              </button>

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
                  {Object.keys(s.labels || {}).map(function (fieldId) {
                    const value = s.answers ? s.answers[fieldId] : "";
                    const text = Array.isArray(value) ? value.join(", ") : value;
                    if (!text) return null;
                    return (
                      <div key={fieldId}>
                        <p className="text-xs text-white/40 mb-1">{s.labels[fieldId]}</p>
                        <p className="text-sm text-white/80 whitespace-pre-wrap">{text}</p>
                      </div>
                    );
                  })}
                  <div className="pt-3 border-t border-white/[0.07] space-y-1">
                    {(s.consents || []).map(function (c) {
                      const granted = s.answers && s.answers[c.id];
                      return (
                        <p key={c.id} className="text-xs">
                          <span className={granted ? "text-green-400" : "text-red-400"}>
                            {granted ? "✓" : "✕"}
                          </span>{" "}
                          <span className="text-white/40">{c.label}</span>
                        </p>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}


function UsersTable({ users, search, roleFilter, stateFilter, onRefresh }) {
  const filtered = users.filter((u) => {
    // Role filter is now server-side, but keep as fallback for search interaction
    if (roleFilter !== "all" && !(u.roles || []).includes(roleFilter)) return false;

    // Text search (client-side only)
    if (search) {
      const q = search.toLowerCase();
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const location = (u.location_display || "").toLowerCase();
      if (!name.includes(q) && !email.includes(q) && !location.includes(q)) return false;
    }
    return true;
  });

  if (filtered.length === 0) {
    return <Card className="bg-white/[0.03] border-white/10 p-8 text-center text-white/40">No users found.</Card>;
  }

  return (
    <div className="space-y-2" data-testid="users-list">
      <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-xs text-white/40 uppercase tracking-wider">
        <div className="col-span-2">User</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-2">Location</div>
        <div className="col-span-1">Profile</div>
        <div className="col-span-1">Roles</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1">Joined</div>
        <div className="col-span-1"></div>
      </div>
      {filtered.map((u) => <UserRow key={u.user_id} u={u} onRefresh={onRefresh} />)}
      <p className="text-white/30 text-xs text-center pt-2">Showing {filtered.length} of {users.length} users</p>
    </div>
  );
}

function UserRow({ u, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const { token } = useAuth();
  const roles = u.roles || [];
  const ap = u.assistant_profile || {};
  const sp = u.studio_profile || {};
  const forHireActive = ap.find_work_active !== false && roles.includes("assistant");
  const studioActive = sp.hiring_active !== false && roles.includes("studio");
  const joinDate = u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "—";
  const isDeactivated = u.account_deactivated === true;
  const locationDisplay = u.location_display || "";
  const profilePct = u.profile_completeness ? u.profile_completeness.percentage : 0;
  const profileColor = profilePct >= 80 ? "text-green-400 bg-green-500/20" : profilePct >= 40 ? "text-yellow-400 bg-yellow-500/20" : "text-red-400 bg-red-500/20";
  const profileBarColor = profilePct >= 80 ? "bg-green-400" : profilePct >= 40 ? "bg-yellow-400" : "bg-red-400";

  // Delete / Deactivate / Cancel Subscription states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteSummary, setDeleteSummary] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  async function handleCancelSubscription() {
    if (!confirm(`Cancel subscription for ${u.name}? They'll keep access until end of billing period.`)) return;
    setCancellingSubscription(true);
    try {
      const res = await fetch(`${API}/admin/users/${u.user_id}/cancel-subscription`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Subscription cancelled");
        if (onRefresh) onRefresh();
      } else {
        toast.error(data.detail || "Failed to cancel subscription");
      }
    } catch {
      toast.error("Network error");
    }
    setCancellingSubscription(false);
  }

  async function handleDelete() {
    if (confirmEmail.toLowerCase() !== u.email.toLowerCase()) {
      toast.error("Email does not match. Please type the exact email to confirm.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(API + "/admin/users/" + u.user_id, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        const data = await res.json();
        setDeleteSummary(data.deleted);
        toast.success("User permanently deleted");
        if (onRefresh) setTimeout(onRefresh, 1500);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete user");
      }
    } catch(e) {
      toast.error("Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeactivate() {
    setActionLoading(true);
    try {
      const res = await fetch(API + "/admin/users/" + u.user_id + "/deactivate", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        toast.success("User deactivated");
        setShowDeactivateDialog(false);
        if (onRefresh) onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to deactivate user");
      }
    } catch(e) {
      toast.error("Failed to deactivate user");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReactivate() {
    setActionLoading(true);
    try {
      const res = await fetch(API + "/admin/users/" + u.user_id + "/reactivate", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        toast.success("User reactivated");
        if (onRefresh) onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to reactivate user");
      }
    } catch(e) {
      toast.error("Failed to reactivate user");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <Card className="bg-white/[0.03] border-white/10 hover:border-white/20 transition-all" data-testid={`user-row-${u.user_id}`}>
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          {/* Name + Avatar */}
          <div className="col-span-2 flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0 overflow-hidden">
              {u.profile_image ? (
                <img src={u.profile_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white/40 text-sm font-bold">{(u.name || "?").charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{u.name || "Unnamed"}</p>
              <p className="text-white/30 text-xs font-mono truncate md:hidden">{u.email}</p>
            </div>
          </div>

          {/* Email */}
          <div className="col-span-3 hidden md:block">
            <a href={`mailto:${u.email}`} className="text-sm text-white/60 hover:text-white truncate block">{u.email}</a>
          </div>

          {/* Location */}
          <div className="col-span-2 hidden md:block">
            {locationDisplay ? (
              <>
                <span className="text-sm text-white/60 truncate block" data-testid={`user-location-${u.user_id}`}>{locationDisplay}</span>
                {u.metro && (
                  <span className="text-[11px] text-white/30 truncate block" data-testid={`user-metro-${u.user_id}`}>{u.metro}</span>
                )}
              </>
            ) : (
              <span className="text-xs text-white/20">No location</span>
            )}
          </div>

          {/* Profile % */}
          <div className="col-span-1 hidden md:block" data-testid={`user-profile-pct-${u.user_id}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden" style={{maxWidth: "48px"}}>
                <div className={`h-full rounded-full ${profileBarColor}`} style={{width: profilePct + "%"}} />
              </div>
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-sm ${profileColor}`}>{profilePct}%</span>
            </div>
          </div>

          {/* Roles */}
          <div className="col-span-1 flex flex-wrap gap-1">
            {roles.includes("studio") && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-[#3498DB]/20 text-[#3498DB]">
                <Building className="w-2.5 h-2.5" />S
              </span>
            )}
            {roles.includes("assistant") && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-coral/20 text-coral">
                <Camera className="w-2.5 h-2.5" />2nd
              </span>
            )}
            {roles.includes("admin") && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-purple-500/20 text-purple-400">
                <Shield className="w-2.5 h-2.5" />A
              </span>
            )}
          </div>

          {/* Status */}
          <div className="col-span-1 flex flex-wrap gap-1">
            {isDeactivated && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-orange-500/20 text-orange-400">Off</span>
            )}
            {!isDeactivated && roles.includes("assistant") && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${forHireActive ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                {forHireActive ? "Hire" : "Off"}
              </span>
            )}
            {!isDeactivated && roles.includes("studio") && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${studioActive ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                {studioActive ? "Hiring" : "Off"}
              </span>
            )}
          </div>

          {/* Joined */}
          <div className="col-span-1 hidden md:block">
            <span className="text-xs text-white/40">{joinDate}</span>
          </div>

          {/* Actions */}
          <div className="col-span-1 flex justify-end">
            <ChevronRight className={`w-4 h-4 text-white/20 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-white/5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 text-sm">
            {/* Profile Details */}
            <div>
              <p className="text-white/30 text-xs uppercase tracking-wider mb-2">Profile</p>
              <div className="space-y-1.5 text-white/60">
                <p>ID: <span className="font-mono text-white/40 text-xs">{u.user_id}</span></p>
                {u.phone && <p>Phone: {u.phone}</p>}
                {u.bio && <p className="line-clamp-2">Bio: {u.bio}</p>}
                {(u.location?.city || u.location?.state) && (
                  <p>Location: {[u.location.city, u.location.state].filter(Boolean).join(", ")}</p>
                )}
              </div>
            </div>

            {/* Assistant Profile */}
            {roles.includes("assistant") && (
              <div>
                <p className="text-white/30 text-xs uppercase tracking-wider mb-2">For-Hire Profile</p>
                <div className="space-y-1.5 text-white/60">
                  {ap.years_experience > 0 && <p>Experience: {ap.years_experience} yrs</p>}
                  {ap.work_types?.length > 0 && <p>Work types: {ap.work_types.map(wt => typeof wt === "string" ? wt : (wt.label || wt.name || wt.value || "")).filter(Boolean).join(", ")}</p>}
                  {(ap.rate_min || ap.rate_max) && <p>Rate: ${ap.rate_min || "?"} - ${ap.rate_max || "?"}/hr</p>}
                  {ap.gear_items?.length > 0 && <p>Gear: {ap.gear_items.length} items</p>}
                  {ap.portfolio?.length > 0 && <p>Portfolio: {ap.portfolio.length} images</p>}
                </div>
              </div>
            )}

            {/* Studio Profile */}
            {roles.includes("studio") && (
              <div>
                <p className="text-white/30 text-xs uppercase tracking-wider mb-2">Studio Profile</p>
                <div className="space-y-1.5 text-white/60">
                  {sp.studio_name && <p>Studio: {sp.studio_name}</p>}
                  {sp.studio_type && <p>Type: {sp.studio_type}</p>}
                  {sp.genres?.length > 0 && <p>Genres: {sp.genres.join(", ")}</p>}
                  {sp.team_size && <p>Team: {sp.team_size}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Action Links */}
          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-white/5">
            <a href={`/profile/${u.user_id}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-white/10 text-white/50 hover:text-white hover:bg-white/5 text-xs" data-testid={`view-profile-${u.user_id}`}>
                <ExternalLink className="w-3 h-3 mr-1" />View Profile
              </Button>
            </a>
            <a href={`mailto:${u.email}`}>
              <Button variant="outline" size="sm" className="border-white/10 text-white/50 hover:text-white hover:bg-white/5 text-xs" data-testid={`email-user-${u.user_id}`}>
                <Mail className="w-3 h-3 mr-1" />Email
              </Button>
            </a>

            <div className="flex-1" />

            <Button
              variant="outline"
              size="sm"
              className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 text-xs"
              onClick={(e) => { e.stopPropagation(); handleCancelSubscription(); }}
              disabled={cancellingSubscription}
              data-testid={`cancel-sub-${u.user_id}`}
            >
              <Tag className="w-3 h-3 mr-1" />{cancellingSubscription ? "Cancelling..." : "Cancel Sub"}
            </Button>

            {isDeactivated ? (
              <Button
                variant="outline"
                size="sm"
                className="border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs"
                onClick={(e) => { e.stopPropagation(); handleReactivate(); }}
                disabled={actionLoading}
                data-testid={`reactivate-user-${u.user_id}`}
              >
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                Reactivate
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 text-xs"
                onClick={(e) => { e.stopPropagation(); setShowDeactivateDialog(true); }}
                data-testid={`deactivate-user-${u.user_id}`}
              >
                <UserMinus className="w-3 h-3 mr-1" />Deactivate
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
              onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); setConfirmEmail(""); setDeleteSummary(null); }}
              data-testid={`delete-user-${u.user_id}`}
            >
              <UserX className="w-3 h-3 mr-1" />Delete
            </Button>
          </div>

          {/* Deactivate Confirmation Dialog */}
          <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
            <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" onClick={(e) => e.stopPropagation()}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <UserMinus className="w-5 h-5 text-orange-400" />
                  Deactivate User
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-white/60 text-sm">
                  This will deactivate <strong className="text-white">{u.name || u.email}</strong>'s account:
                </p>
                <ul className="text-white/50 text-sm space-y-1 pl-4 list-disc">
                  <li>User won't appear in candidate searches</li>
                  <li>User won't be able to log in</li>
                  <li>Stripe subscription will be cancelled</li>
                  <li>Contracts, messages, and job history will be <strong className="text-white">retained</strong></li>
                  <li>Account can be reactivated later</li>
                </ul>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                    onClick={() => setShowDeactivateDialog(false)}
                    data-testid="deactivate-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                    onClick={handleDeactivate}
                    disabled={actionLoading}
                    data-testid="deactivate-confirm-btn"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Deactivate User"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={(o) => { if (!deleteSummary) setShowDeleteDialog(o); }}>
            <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" onClick={(e) => e.stopPropagation()}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Permanently Delete User
                </DialogTitle>
              </DialogHeader>

              {deleteSummary ? (
                <div className="space-y-4">
                  <p className="text-green-400 text-sm font-medium">User permanently deleted.</p>
                  <div className="bg-white/[0.03] rounded p-3 space-y-1">
                    {Object.entries(deleteSummary).map(([key, val]) => (
                      val > 0 || val === true ? (
                        <p key={key} className="text-white/50 text-xs flex justify-between">
                          <span>{key.replace(/_/g, " ")}</span>
                          <span className="text-white/70 font-mono">{val === true ? "Yes" : val}</span>
                        </p>
                      ) : null
                    ))}
                  </div>
                  <Button
                    className="w-full bg-white/10 hover:bg-white/20 text-white"
                    onClick={() => setShowDeleteDialog(false)}
                    data-testid="delete-done-btn"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
                    <p className="text-red-400 text-sm font-medium">This action is irreversible.</p>
                    <p className="text-red-400/70 text-xs mt-1">ALL data for this user will be permanently erased including profile, reviews, messages, contracts, jobs, and applications.</p>
                  </div>
                  <p className="text-white/60 text-sm">
                    To confirm, type <strong className="text-white font-mono">{u.email}</strong> below:
                  </p>
                  <Input
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder={u.email}
                    className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 font-mono text-sm"
                    data-testid="delete-confirm-email-input"
                  />
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 border-white/20 text-white hover:bg-white/10"
                      onClick={() => setShowDeleteDialog(false)}
                      data-testid="delete-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                      onClick={handleDelete}
                      disabled={actionLoading || confirmEmail.toLowerCase() !== u.email.toLowerCase()}
                      data-testid="delete-confirm-btn"
                    >
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete Permanently"}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Card>
  );
}

/* ── Jobs Panel (admin) ────────────────────── */
function JobsPanel({ jobs, search, setSearch, statusFilter, setStatusFilter, loading }) {
  const filtered = (jobs || []).filter((j) => {
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (j.title || "").toLowerCase().includes(s) ||
      (j.city || "").toLowerCase().includes(s) ||
      (j.creator_name || "").toLowerCase().includes(s) ||
      (j.creator_email || "").toLowerCase().includes(s) ||
      (j.creator_studio_name || "").toLowerCase().includes(s)
    );
  });

  const statuses = ["all", "open", "pending_confirmation", "filled", "completed", "expired", "withdrawn"];

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d).slice(0, 10);
      return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch { return String(d).slice(0, 10); }
  };

  const statusBadge = (s) => {
    const map = {
      open: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
      pending_confirmation: "bg-amber-500/15 text-amber-300 border-amber-500/25",
      filled: "bg-blue-500/15 text-blue-300 border-blue-500/25",
      completed: "bg-purple-500/15 text-purple-300 border-purple-500/25",
      expired: "bg-white/10 text-white/50 border-white/15",
      withdrawn: "bg-red-500/15 text-red-300 border-red-500/25",
    };
    return map[s] || "bg-white/10 text-white/60 border-white/15";
  };

  return (
    <div data-testid="jobs-panel">
      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, city, studio, or email..."
            className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm"
            data-testid="jobs-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white/[0.05] border border-white/10 text-white h-10 rounded-sm px-3 text-sm"
          data-testid="jobs-status-filter"
        >
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? "All Statuses" : s.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </div>

      <div className="text-sm text-white/50 mb-3" data-testid="jobs-count">
        Showing {filtered.length} of {jobs.length} jobs
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white/[0.02] border-white/10 p-8 text-center text-white/50" data-testid="jobs-empty">
          No jobs match your filters.
        </Card>
      ) : (
        <Card className="bg-white/[0.02] border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="jobs-table">
              <thead className="bg-white/[0.04] text-white/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-4 py-3 font-medium">Job Title</th>
                  <th className="text-left px-4 py-3 font-medium">City</th>
                  <th className="text-left px-4 py-3 font-medium">Posted By</th>
                  <th className="text-right px-4 py-3 font-medium">Candidates</th>
                  <th className="text-center px-4 py-3 font-medium">Status</th>
                  <th className="text-center px-4 py-3 font-medium">Hired</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((j) => (
                  <tr key={j.job_id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`job-row-${j.job_id}`}>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{formatDate(j.created_at)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/jobs/${j.job_id}`} className="text-white hover:text-[#FF6B6B] font-medium">
                        {j.title}
                      </Link>
                      {j.event_date && (
                        <div className="text-xs text-white/40 mt-0.5">Event: {formatDate(j.event_date)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {j.city ? `${j.city}${j.state ? `, ${j.state}` : ""}` : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white/90">{j.creator_studio_name || j.creator_name || <span className="text-white/30">Unknown</span>}</div>
                      {j.creator_email && (
                        <a href={`mailto:${j.creator_email}`} className="text-xs text-white/50 hover:text-[#FF6B6B]" data-testid={`job-creator-email-${j.job_id}`}>
                          {j.creator_email}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-white/80 font-medium" data-testid={`job-match-count-${j.job_id}`}>
                      {j.match_count ?? 0}
                      {j.applicant_count > 0 && (
                        <div className="text-xs text-white/40 font-normal mt-0.5">
                          {j.applicant_count} applied
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-sm border capitalize ${statusBadge(j.status)}`}>
                        {(j.status || "—").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {j.is_marked_hired ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400 inline" data-testid={`job-hired-yes-${j.job_id}`} />
                      ) : (
                        <span className="text-white/25" data-testid={`job-hired-no-${j.job_id}`}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

